import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft,
  ArrowRight,
  Box,
  FileBox,
  Check,
  ChevronDown,
  ImagePlus,
  Images,
  Loader2,
  Nfc,
  PackageOpen,
  Palette,
  PanelsTopLeft,
  Plus,
  QrCode,
  Save,
  Settings2,
  Square,
  Trash2,
  Type,
} from "lucide-react";
import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";

import { qrPrintApi } from "../../api/qrPrintApi";
import QrPrint3DProductEditor from "../../components/qrPrint/3d/QrPrint3DProductEditor";
import {
  DEFAULT_QR_EDITOR_SCHEMA,
  makeQrEditorBlock,
  normalizeQrEditorSchema,
} from "../../components/qrPrint/admin/qrEditorSchema.jsx";
import { getQrProductPresetDefinition } from "../../components/qrPrint/3d/qrProductPresets";
import { useLanguage } from "../../contexts/LanguageContext";

import "../../styles/QrPrint/QrPrintProductForm.css";

const translateFallback = (
  t,
  key,
  fallback,
  params = {},
) => {
  const value = t(
    key,
    params,
  );

  if (value !== key) {
    return value;
  }

  return Object.entries(
    params,
  ).reduce(
    (result, [paramKey, paramValue]) =>
      String(result).replaceAll(
        `{{${paramKey}}}`,
        String(paramValue ?? ""),
      ),
    fallback,
  );
};

// Fetch saved images through the authenticated API (avoids public-storage CORS).
function SavedProductImage({ productId, imageType, imageIndex = 0, alt }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!productId) return undefined;
    let cancelled = false;
    let objectUrl = null;
    setImageUrl(null);
    setFailed(false);
    qrPrintApi.getProductImage(productId, imageType, imageIndex)
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setImageUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [productId, imageType, imageIndex]);

  if (failed) return <span className="qr-print-image-placeholder">Preview unavailable</span>;
  if (!imageUrl) return <span className="qr-print-image-placeholder">Loading image…</span>;
  return <img src={imageUrl} alt={alt} className="qr-print-image-thumb" />;
}

function NewProductImage({ file, alt }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) return undefined;
    const nextUrl = URL.createObjectURL(file);
    setUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);
  return url ? <img src={url} alt={alt} className="qr-print-image-thumb" /> : null;
}

const PRIMARY_COLOR = "#b45309";
const PRIMARY_SOFT = "#fff8ee";

const makeOption = () => ({
  value: "",
  label: "",
});

const makeColor = () => ({
  value: "",
  label: "",
  hex: "#ffffff",
});

const default3DDefinition =
  getQrProductPresetDefinition(
    "premium_acrylic",
  );

const default3DSettings = {
  allow_material: true,
  allow_color: true,
  allow_finish: true,
  allow_thickness: true,
  allow_angle: true,
  allow_qr_size: true,
  allow_qr_colors: true,
  allow_design_variant: true,
  allow_qr_frame: true,
  allow_accent_mode: true,
};

const PRODUCT_TYPES = [
  ["table_stand", "Table stand"],
  ["table_plate", "Table plate"],
  ["counter_display", "Counter display"],
  ["menu_holder", "Menu holder"],
  ["wall_sign", "Wall sign"],
];

const PRINT_SIZE_MIN_CM = 3;
const PRINT_SIZE_MAX_CM = 20;

const getFileName = (
  file,
) => {
  if (!file) {
    return "";
  }

  return file.name || "";
};

const clampNumber = (
  value,
  min,
  max,
  fallback = min,
) => {
  const parsed =
    Number(value);

  if (
    !Number.isFinite(parsed)
  ) {
    return fallback;
  }

  return Math.min(
    max,
    Math.max(min, parsed),
  );
};

const cloneDefaultValue = (
  value,
) => {
  if (
    value === undefined ||
    value === null
  ) {
    return {};
  }

  if (
    typeof value !==
    "object"
  ) {
    return value;
  }

  return JSON.parse(
    JSON.stringify(value),
  );
};

const makeNfcEditorBlock =
  (t) => ({
    id: "main-nfc",
    type: "nfc",
    label: translateFallback(
      t,
      "qrPrint.productForm.designStudio.nfcTapZone",
      "NFC tap zone",
    ),
    enabled: true,
    required: true,

    default: {
      x: 50,
      y: 66,
      size: 22,
      color: PRIMARY_COLOR,
      background_color:
        PRIMARY_SOFT,
    },

    settings: {
      movable: true,
      resizable: true,
      color_editable: true,
      min_size: 12,
      max_size: 42,
    },
  });

const makeAccessBlock = (
  type,
  t,
) => {
  if (type === "nfc") {
    return {
      ...makeNfcEditorBlock(t),
      required: false,
    };
  }

  return makeQrEditorBlock(
    type,
  );
};

const formatProductTypeLabel = (
  t,
  value,
) => {
  const safeValue =
    value || "table_stand";

  return translateFallback(
    t,
    `qrPrint.types.${safeValue}`,
    String(safeValue)
      .replace(/_/g, " ")
      .replace(
        /\b\w/g,
        (letter) =>
          letter.toUpperCase(),
      ),
  );
};


const getEditorBlockDisplayLabel = (
  t,
  block,
) => {
  if (!block) {
    return "";
  }

  const knownLabels = {
    qr: [
      "qrPrint.productForm.designStudio.qrCode",
      "QR code",
    ],
    nfc: [
      "qrPrint.productForm.designStudio.nfcTapZone",
      "NFC tap zone",
    ],
    shape: [
      "qrPrint.productForm.designStudio.shape",
      "Shape",
    ],
    logo: [
      "qrPrint.productForm.designStudio.logoLabel",
      "Logo",
    ],
    image: [
      "qrPrint.productForm.designStudio.imageLabel",
      "Image",
    ],
    background_color: [
      "qrPrint.productForm.designStudio.backgroundColor",
      "Background color",
    ],
    product_color: [
      "qrPrint.productForm.designStudio.productBorderColor",
      "Product border color",
    ],
  };

  if (
    block.id ===
    "restaurant-name"
  ) {
    return translateFallback(
      t,
      "qrPrint.productForm.designStudio.restaurantName",
      "Restaurant name",
    );
  }

  if (
    block.type === "text" &&
    [
      "Custom text",
      "New text",
      "Text",
    ].includes(block.label)
  ) {
    return translateFallback(
      t,
      "qrPrint.productForm.designStudio.customText",
      "Custom text",
    );
  }

  const definition =
    knownLabels[
      block.type
    ];

  if (definition) {
    return translateFallback(
      t,
      definition[0],
      definition[1],
    );
  }

  return (
    block.label ||
    block.id ||
    block.type ||
    ""
  );
};

const getDefaultPrintSizeCm = (
  productType,
) => {
  if (
    productType ===
    "wall_sign"
  ) {
    return {
      width: 20,
      height: 20,
    };
  }

  if (
    productType ===
    "counter_display"
  ) {
    return {
      width: 15,
      height: 20,
    };
  }

  if (
    productType ===
      "table_stand" ||
    productType ===
      "menu_holder"
  ) {
    return {
      width: 12,
      height: 18,
    };
  }

  return {
    width: 15,
    height: 10,
  };
};

const normalizePrintSizeCm = (
  editorSchema,
  productType,
) => {
  const fallback =
    getDefaultPrintSizeCm(
      productType,
    );

  const canvas =
    editorSchema?.canvas ||
    editorSchema?.print_size ||
    {};

  return {
    width: clampNumber(
      canvas.width_cm,
      PRINT_SIZE_MIN_CM,
      PRINT_SIZE_MAX_CM,
      Math.min(
        Math.max(
          fallback.width,
          PRINT_SIZE_MIN_CM,
        ),
        PRINT_SIZE_MAX_CM,
      ),
    ),

    height: clampNumber(
      canvas.height_cm,
      PRINT_SIZE_MIN_CM,
      PRINT_SIZE_MAX_CM,
      Math.min(
        Math.max(
          fallback.height,
          PRINT_SIZE_MIN_CM,
        ),
        PRINT_SIZE_MAX_CM,
      ),
    ),
  };
};

const formatCm = (
  value,
) => {
  const number =
    Number(value);

  if (
    !Number.isFinite(number)
  ) {
    return "0cm";
  }

  return `${Number(
    number.toFixed(2),
  )}cm`;
};

const getSchemaElementValue = (
  block,
) =>
  cloneDefaultValue(
    block.default ?? {},
  );

const getTextTransform = (
  align,
) => {
  if (align === "left") {
    return "translate(0, -50%)";
  }

  if (
    align === "right"
  ) {
    return "translate(-100%, -50%)";
  }

  return "translate(-50%, -50%)";
};

const roundSchemaNumber = (
  value,
) =>
  Math.round(
    Number(value) * 100,
  ) / 100;

const normalizeHexColor = (
  value,
  fallback,
) => {
  if (
    typeof value ===
      "string" &&
    /^#[0-9a-f]{6}$/i.test(
      value,
    )
  ) {
    return value;
  }

  return fallback;
};

const getDragPadding = (
  block,
  value,
) => {
  if (block.type === "qr") {
    const half =
      clampNumber(
        value.size,
        12,
        72,
        34,
      ) / 2;

    return {
      x: half,
      y: half,
    };
  }

  if (
    block.type === "logo" ||
    block.type === "image"
  ) {
    const half =
      clampNumber(
        value.width,
        6,
        70,
        24,
      ) / 2;

    return {
      x: half,
      y: half,
    };
  }

  if (
    block.type === "nfc"
  ) {
    const half =
      clampNumber(
        value.size,
        10,
        48,
        22,
      ) / 2;

    return {
      x: half,
      y: half,
    };
  }

  if (
    block.type === "shape"
  ) {
    return {
      x:
        clampNumber(
          value.width,
          2,
          100,
          28,
        ) / 2,

      y:
        clampNumber(
          value.height,
          1,
          100,
          12,
        ) / 2,
    };
  }

  return {
    x: 3,
    y: 3,
  };
};

const makeControlled2DEditorBlock = (
  type,
  t,
) => {
  const id = `${type}-${Date.now()}-${Math.round(
    Math.random() *
      9999,
  )}`;

  if (type === "text") {
    return {
      id,
      type: "text",
      label: translateFallback(
        t,
        "qrPrint.productForm.designStudio.customText",
        "Custom text",
      ),
      enabled: true,
      required: false,

      default: {
        text: translateFallback(
          t,
          "qrPrint.productForm.designStudio.newText",
          "New text",
        ),
        x: 50,
        y: 34,
        font_size: 3.6,
        color: "#1f2937",
        align: "center",
        weight: 700,
      },

      settings: {
        text_editable: true,
        movable: true,
        resizable: true,
        color_editable: true,
        font_editable: true,
        align_editable: true,
      },
    };
  }

  if (type === "qr") {
    const block =
      makeQrEditorBlock(
        "qr",
      );

    return {
      ...block,

      id,
      label: translateFallback(
        t,
        "qrPrint.productForm.designStudio.qrCode",
        "QR code",
      ),
      required: false,

      default: {
        ...(typeof block.default ===
          "object" &&
        block.default !==
          null
          ? block.default
          : {}),

        x: 50,
        y: 55,
      },
    };
  }

  if (type === "nfc") {
    return {
      ...makeNfcEditorBlock(t),
      id,
      required: false,
    };
  }

  return {
    id,
    type: "shape",
    label: translateFallback(
      t,
      "qrPrint.productForm.designStudio.shape",
      "Shape",
    ),
    enabled: true,
    required: false,

    default: {
      shape: "rectangle",
      x: 50,
      y: 50,
      width: 28,
      height: 12,
      radius: 14,
      fill_color:
        PRIMARY_SOFT,
      border_color:
        PRIMARY_COLOR,
      border_width: 1,
      opacity: 1,
    },

    settings: {
      movable: true,
      resizable: true,
      color_editable: true,
    },
  };
};

function QrPrintFormSelect({
  value,
  options,
  onChange,
  label,
}) {
  const selectRef =
    useRef(null);

  const [
    open,
    setOpen,
  ] = useState(false);

  const selectedOption =
    options.find(
      (option) =>
        String(
          option.value,
        ) === String(value),
    ) || options[0];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick =
      (event) => {
        if (
          selectRef.current &&
          !selectRef.current.contains(
            event.target,
          )
        ) {
          setOpen(false);
        }
      };

    const handleEscape = (
      event,
    ) => {
      if (
        event.key ===
        "Escape"
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick,
    );

    document.addEventListener(
      "keydown",
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick,
      );

      document.removeEventListener(
        "keydown",
        handleEscape,
      );
    };
  }, [open]);

  return (
    <div
      ref={selectRef}
      className={`qr-print-form-select ${
        open
          ? "qr-print-form-select--open"
          : ""
      }`}
    >
      <button
        type="button"
        className="qr-print-form-select__trigger"
        onClick={() =>
          setOpen(
            (current) =>
              !current,
          )
        }
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>
          {selectedOption?.label ||
            label}
        </span>

        <ChevronDown
          size={17}
        />
      </button>

      {open && (
        <div
          className="qr-print-form-select__options"
          role="listbox"
        >
          {options.map(
            (option) => {
              const active =
                String(
                  option.value,
                ) ===
                String(value);

              return (
                <button
                  key={
                    option.value
                  }
                  type="button"
                  role="option"
                  aria-selected={
                    active
                  }
                  className={`qr-print-form-select__option ${
                    active
                      ? "qr-print-form-select__option--active"
                      : ""
                  }`}
                  onClick={() => {
                    onChange(
                      option.value,
                    );

                    setOpen(
                      false,
                    );
                  }}
                >
                  <span>
                    {
                      option.label
                    }
                  </span>

                  {active && (
                    <Check
                      size={15}
                    />
                  )}
                </button>
              );
            },
          )}
        </div>
      )}
    </div>
  );
}

/** Local GLB inspection. Rendering and validation happen in the browser. */
function LocalGlbPreview({ file, existingUrl, productId }) {
  const mountRef = useRef(null);
  const resetViewRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!file && !existingUrl) {
      setStatus('idle');
      setError('');
      setDetails(null);
      return undefined;
    }

    const mount = mountRef.current;
    if (!mount) return undefined;
    let cancelled = false;
    let frame = 0;
    let resizeObserver;
    let renderer;
    let controls;
    let displayedModel;
    let objectUrl;
    resetViewRef.current = null;
    setStatus('loading');
    setError('');
    setDetails(null);

    const load = async () => {
      try {
        // Dynamic imports avoid loading the 3D engine on forms without a model.
        const THREE = await import('three');
        const { GLTFLoader } = await import('three/addons/loaders/GLTFLoader.js');
        const { OrbitControls } = await import('three/addons/controls/OrbitControls.js');
        if (cancelled) return;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color('#fff8ee');
        const camera = new THREE.PerspectiveCamera(42, 1, 0.001, 10000);
        renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.4;
        mount.appendChild(renderer.domElement);
        renderer.domElement.setAttribute('aria-label', 'Interactive 3D model viewer');

        scene.add(new THREE.HemisphereLight(0xffffff, 0xc6aa8e, 2.5));
        const key = new THREE.DirectionalLight(0xffffff, 2.6);
        key.position.set(4, 7, 5);
        scene.add(key);
        const fill = new THREE.DirectionalLight(0xffffff, 1.1);
        fill.position.set(-4, 3, -4);
        scene.add(fill);

        controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.07;
        controls.enablePan = true;

        const resize = () => {
          if (!renderer || !mount) return;
          const width = Math.max(1, mount.clientWidth);
          const height = Math.max(1, mount.clientHeight);
          renderer.setSize(width, height, false);
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
        };
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(mount);
        resize();

        if (file) {
          if (!/\.glb$/i.test(file.name) || file.size < 20 || file.size > 50 * 1024 * 1024) {
            throw new Error('Select a GLB file smaller than 50 MB.');
          }
          const header = new DataView(await file.slice(0, 12).arrayBuffer());
          if (header.getUint32(0, true) !== 0x46546c67 || header.getUint32(4, true) !== 2 || header.getUint32(8, true) !== file.size) {
            throw new Error('The selected file is not a valid GLB 2.0 file.');
          }
          objectUrl = URL.createObjectURL(file);
        } else if (productId && existingUrl) {
          // The direct storage URL is blocked by CORS; use the authenticated API.
          const response = await qrPrintApi.getProduct3DModelFile(productId);
          if (cancelled) return;
          const modelBlob = response.data;
          if (!(modelBlob instanceof Blob) || modelBlob.size < 20) {
            throw new Error('The saved GLB response is empty or invalid.');
          }
          const header = new DataView(await modelBlob.slice(0, 12).arrayBuffer());
          if (header.getUint32(0, true) !== 0x46546c67 || header.getUint32(4, true) !== 2 || header.getUint32(8, true) !== modelBlob.size) {
            throw new Error('The API returned an invalid GLB file. Check authentication and the model-file route.');
          }
          objectUrl = URL.createObjectURL(modelBlob);
        }
        if (cancelled) return;

        const gltf = await new GLTFLoader().loadAsync(objectUrl || existingUrl);
        displayedModel = gltf.scene;
        if (cancelled) return;
        scene.add(displayedModel);
        displayedModel.updateMatrixWorld(true);
        const bounds = new THREE.Box3().setFromObject(displayedModel);
        if (bounds.isEmpty()) throw new Error('This GLB does not contain visible geometry.');

        let meshes = 0;
        let triangles = 0;
        displayedModel.traverse((obj) => {
          if (!obj.isMesh) return;
          meshes += 1;
          const geo = obj.geometry;
          if (geo?.index) triangles += Math.floor(geo.index.count / 3);
          else if (geo?.attributes?.position) triangles += Math.floor(geo.attributes.position.count / 3);
        });
        if (meshes === 0) throw new Error('No renderable meshes were found in this GLB.');
        const center = bounds.getCenter(new THREE.Vector3());
        const size = bounds.getSize(new THREE.Vector3());
        const radius = Math.max(size.length() * 0.5, 0.001);
        const distance = radius / Math.sin(THREE.MathUtils.degToRad(camera.fov * 0.5)) * 1.35;
        const initialPosition = center.clone().add(new THREE.Vector3(distance * 0.6, distance * 0.45, distance));
        camera.near = Math.max(0.0001, distance / 10000);
        camera.far = Math.max(100, distance * 100);
        camera.updateProjectionMatrix();
        controls.minDistance = radius * 0.08;
        controls.maxDistance = distance * 20;
        const reset = () => {
          camera.position.copy(initialPosition);
          controls.target.copy(center);
          controls.update();
        };
        resetViewRef.current = reset;
        reset();
        setDetails({ meshes, triangles, dimensions: size.toArray().map(n => Number(n.toFixed(3))) });
        setStatus('ready');

        const animate = () => {
          if (cancelled) return;
          frame = requestAnimationFrame(animate);
          controls.update();
          renderer.render(scene, camera);
        };
        animate();
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setError(err?.message || 'Unable to display this GLB model.');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
      resetViewRef.current = null;
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      controls?.dispose();
      displayedModel?.traverse((obj) => {
        obj.geometry?.dispose?.();
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
        materials.forEach((material) => {
          if (!material) return;
          Object.values(material).forEach(value => { if (value?.isTexture) value.dispose(); });
          material.dispose?.();
        });
      });
      renderer?.dispose();
      renderer?.domElement?.remove();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, existingUrl, productId]);

  if (!file && !existingUrl) return null;
  return (
    <div className="qr-print-glb-local-preview">
      <div className="qr-print-glb-local-preview__heading">
        <div className="qr-print-glb-local-preview__title">
          <Box size={19} />
          <div><strong>3D model inspection</strong><span>{status === 'ready' ? 'Drag to rotate · Scroll to zoom · Right-drag to pan' : status === 'loading' ? 'Reading the 3D scene…' : status === 'error' ? 'Preview unavailable' : 'Preparing preview'}</span></div>
        </div>
        <button type="button" className="qr-print-glb-local-preview__reset" onClick={() => resetViewRef.current?.()} disabled={status !== 'ready'}>Reset view</button>
      </div>
      <div className="qr-print-glb-local-preview__stage">
        <div className="qr-print-glb-local-preview__canvas" ref={mountRef} aria-label="3D model preview" />
        {status === 'loading' && <div className="qr-print-glb-local-preview__overlay"><Loader2 size={25} className="qr-print-glb-local-preview__spinner" /><span>Inspecting 3D file…</span></div>}
        {status === 'error' && <div className="qr-print-glb-local-preview__overlay is-error" role="alert">{error}</div>}
      </div>
      {details && status === 'ready' && (
        <div className="qr-print-glb-local-preview__stats">
          <span><b>{details.meshes.toLocaleString()}</b> meshes</span>
          <span><b>{details.triangles.toLocaleString()}</b> triangles</span>
          <span><b>{details.dimensions.join(' × ')}</b> model units</span>
        </div>
      )}
      <small>Previewed locally before saving. Dimensions use the units stored in the GLB file.</small>
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  title,
  description,
  action = null,
}) {
  return (
    <div className="qr-print-form-section__title">
      <span className="qr-print-form-section__icon">
        <Icon size={20} />
      </span>

      <div className="qr-print-form-section__title-copy">
        <h2>{title}</h2>

        {description ? (
          <p>{description}</p>
        ) : null}
      </div>

      {action ? (
        <div className="qr-print-form-section__title-action">
          {action}
        </div>
      ) : null}
    </div>
  );
}

export default function QrPrintProductForm() {
  const { t } =
    useLanguage();

  const navigate =
    useNavigate();

  const { productId } =
    useParams();

  const editing =
    Boolean(productId);

  const [
    direction,
    setDirection,
  ] = useState(
    document
      .documentElement
      .dir || "ltr",
  );

  const isRtl =
    direction === "rtl";

  const [
    loading,
    setLoading,
  ] = useState(editing);

  const [
    saving,
    setSaving,
  ] = useState(false);
  const [glbUploadError, setGlbUploadError] = useState('');

  const [
    form,
    setForm,
  ] = useState({
    name: "",
    product_type:
      "table_stand",

    access_type: "qr",

    supports_front_design:
      true,

    supports_back_design:
      false,

    nfc_chip_type:
      "NTAG213",

    nfc_writable: true,

    nfc_lock_after_write:
      false,

    nfc_notes: "",

    description: "",

    base_unit_price: "",

    minimum_quantity: 1,

    sort_order: 0,

    is_recommended: false,

    is_active: true,

    available_sizes: [
      makeOption(),
    ],

    available_materials: [
      makeOption(),
    ],

    available_colors: [
      makeColor(),
    ],

    recommendation_tags: [],

    editor_schema:
      DEFAULT_QR_EDITOR_SCHEMA,

    customization_fields: [
      "restaurant_logo",
      "restaurant_name",
      "custom_text",
      "background_color",
      "text_color",
      "accent_color",
      "qr_position",
      "qr_size",
    ],

    preview_mode: "both",

    three_d_source:
      "procedural",

    three_d_definition:
      default3DDefinition,

    three_d_settings:
      default3DSettings,

    three_d_model: null,

    three_d_model_url: null,

    remove_three_d_model:
      false,

    main_image: null,

    gallery_images: [],

    saved_main_image_path: null,
    saved_gallery: [],
    remove_main_image: false,
    remove_gallery_images: [],
  });

  // Keep page direction synchronized with the active application language.
  useEffect(() => {
    const updateDirection =
      () => {
        setDirection(
          document
            .documentElement
            .dir || "ltr",
        );
      };

    updateDirection();

    const observer =
      new MutationObserver(
        updateDirection,
      );

    observer.observe(
      document.documentElement,
      {
        attributes: true,

        attributeFilter: [
          "dir",
          "lang",
        ],
      },
    );

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    document.body.classList.add(
      "qr-print-product-form-route",
    );

    return () => {
      document.body.classList.remove(
        "qr-print-product-form-route",
      );
    };
  }, []);

  useEffect(() => {
    if (!editing) {
      return;
    }

    const load =
      async () => {
        try {
          const response =
            await qrPrintApi.getProduct(
              productId,
            );

          const product =
            response.data
              ?.data || {};

          setForm(
            (current) => ({
              ...current,
              ...product,

              base_unit_price:
                product.base_unit_price ??
                "",

              access_type:
                product.access_type ||
                "qr",

              supports_front_design:
                product.supports_front_design !==
                false,

              supports_back_design:
                product.supports_back_design ===
                true,

              nfc_chip_type:
                product.nfc_chip_type ||
                "NTAG213",

              nfc_writable:
                product.nfc_writable !==
                false,

              nfc_lock_after_write:
                product.nfc_lock_after_write ===
                true,

              nfc_notes:
                product.nfc_notes ||
                "",

              available_sizes:
                product
                  .available_sizes
                  ?.length > 0
                  ? product.available_sizes
                  : [
                      makeOption(),
                    ],

              available_materials:
                product
                  .available_materials
                  ?.length > 0
                  ? product.available_materials
                  : [
                      makeOption(),
                    ],

              available_colors:
                product
                  .available_colors
                  ?.length > 0
                  ? product.available_colors
                  : [
                      makeColor(),
                    ],

              recommendation_tags:
                product.recommendation_tags ||
                [],

              customization_fields:
                product.customization_fields ||
                current.customization_fields,

              editor_schema:
                normalizeQrEditorSchema(
                  product.editor_schema,
                ),

              preview_mode:
                product.preview_mode ||
                "both",

              three_d_source:
                (product.three_d_model_url
                  ? "model"
                  : product.three_d_source || "procedural"),

              three_d_definition:
                product.three_d_definition ||
                default3DDefinition,

              three_d_settings:
                product.three_d_settings ||
                default3DSettings,

              three_d_model:
                null,

              three_d_model_url:
                product.three_d_model_url ||
                null,

              remove_three_d_model:
                false,

              main_image: null,
              gallery_images: [],
              saved_main_image_path: product.main_image_path || null,
              saved_gallery: Array.isArray(product.gallery) ? product.gallery : [],
              remove_main_image: false,
              remove_gallery_images: [],
            }),
          );
        } finally {
          setLoading(
            false,
          );
        }
      };

    load();
  }, [
    editing,
    productId,
  ]);

  const update = (
    key,
    value,
  ) => {
    setForm(
      (current) => ({
        ...current,
        [key]: value,
      }),
    );
  };

  const updateAccessType = (
    accessType,
  ) => {
    setForm((current) => {
      const normalizedSchema =
        normalizeQrEditorSchema(
          current.editor_schema,
        );

      let blocks =
        normalizedSchema.blocks.map(
          (block) => ({
            ...block,
          }),
        );

      if (
        accessType ===
        "nfc"
      ) {
        blocks = blocks.map(
          (block) =>
            block.type ===
            "qr"
              ? {
                  ...block,
                  enabled:
                    false,
                  required:
                    false,
                }
              : block,
        );

        if (
          !blocks.some(
            (block) =>
              block.type ===
              "nfc",
          )
        ) {
          blocks.push(
            makeAccessBlock(
              "nfc",
              t,
            ),
          );
        }

        blocks = blocks.map(
          (block) =>
            block.type ===
            "nfc"
              ? {
                  ...block,
                  enabled:
                    true,
                }
              : block,
        );
      } else if (
        accessType ===
        "qr"
      ) {
        blocks = blocks.map(
          (block) =>
            block.type ===
            "nfc"
              ? {
                  ...block,
                  enabled:
                    false,
                  required:
                    false,
                }
              : block,
        );

        if (
          !blocks.some(
            (block) =>
              block.type ===
              "qr",
          )
        ) {
          blocks.push(
            makeQrEditorBlock(
              "qr",
            ),
          );
        } else {
          blocks =
            blocks.map(
              (block) =>
                block.type ===
                "qr"
                  ? {
                      ...block,
                      enabled:
                        true,
                    }
                  : block,
            );
        }
      } else {
        if (
          !blocks.some(
            (block) =>
              block.type ===
              "qr",
          )
        ) {
          blocks.push(
            makeQrEditorBlock(
              "qr",
            ),
          );
        }

        if (
          !blocks.some(
            (block) =>
              block.type ===
              "nfc",
          )
        ) {
          blocks.push(
            makeAccessBlock(
              "nfc",
              t,
            ),
          );
        }

        blocks =
          blocks.map(
            (block) =>
              [
                "qr",
                "nfc",
              ].includes(
                block.type,
              )
                ? {
                    ...block,
                    enabled:
                      true,
                  }
                : block,
          );
      }

      return {
        ...current,

        access_type:
          accessType,

        editor_schema: {
          ...normalizedSchema,

          ...(current
            .editor_schema
            ?.canvas
            ? {
                canvas:
                  current
                    .editor_schema
                    .canvas,
              }
            : {}),

          blocks,
        },
      };
    });
  };

  const updateEditorBlockDefault = (
    blockId,
    patch,
  ) => {
    setForm((current) => {
      const normalizedSchema =
        normalizeQrEditorSchema(
          current.editor_schema,
        );

      const schemaBlocks =
        Array.isArray(
          current
            .editor_schema
            ?.blocks,
        )
          ? current
              .editor_schema
              .blocks
          : normalizedSchema.blocks;

      return {
        ...current,

        editor_schema: {
          ...normalizedSchema,

          ...(current
            .editor_schema
            ?.canvas
            ? {
                canvas:
                  current
                    .editor_schema
                    .canvas,
              }
            : {}),

          blocks:
            schemaBlocks.map(
              (block) => {
                if (
                  block.id !==
                  blockId
                ) {
                  return block;
                }

                return {
                  ...block,

                  default: {
                    ...(typeof block.default ===
                      "object" &&
                    block.default !==
                      null
                      ? block.default
                      : {}),

                    ...patch,
                  },
                };
              },
            ),
        },
      };
    });
  };

  const replaceEditorBlockDefault = (
    blockId,
    value,
  ) => {
    setForm((current) => {
      const normalizedSchema =
        normalizeQrEditorSchema(
          current.editor_schema,
        );

      const schemaBlocks =
        Array.isArray(
          current
            .editor_schema
            ?.blocks,
        )
          ? current
              .editor_schema
              .blocks
          : normalizedSchema.blocks;

      return {
        ...current,

        editor_schema: {
          ...normalizedSchema,

          ...(current
            .editor_schema
            ?.canvas
            ? {
                canvas:
                  current
                    .editor_schema
                    .canvas,
              }
            : {}),

          blocks:
            schemaBlocks.map(
              (block) =>
                block.id ===
                blockId
                  ? {
                      ...block,
                      default:
                        value,
                    }
                  : block,
            ),
        },
      };
    });
  };

  const updateEditorCanvasSize = (
    patch,
  ) => {
    setForm((current) => {
      const normalizedSchema =
        normalizeQrEditorSchema(
          current.editor_schema,
        );

      const currentSize =
        normalizePrintSizeCm(
          current.editor_schema,
          current.product_type,
        );

      return {
        ...current,

        editor_schema: {
          ...normalizedSchema,

          canvas: {
            ...(typeof current
              .editor_schema
              ?.canvas ===
              "object" &&
            current.editor_schema
              .canvas !== null
              ? current
                  .editor_schema
                  .canvas
              : {}),

            width_cm:
              clampNumber(
                patch.width_cm ??
                  currentSize.width,
                PRINT_SIZE_MIN_CM,
                PRINT_SIZE_MAX_CM,
                currentSize.width,
              ),

            height_cm:
              clampNumber(
                patch.height_cm ??
                  currentSize.height,
                PRINT_SIZE_MIN_CM,
                PRINT_SIZE_MAX_CM,
                currentSize.height,
              ),
          },
        },
      };
    });
  };

  const addEditorBlock = (
    type,
  ) => {
    setForm((current) => {
      const normalizedSchema =
        normalizeQrEditorSchema(
          current.editor_schema,
        );

      const schemaBlocks =
        Array.isArray(
          current
            .editor_schema
            ?.blocks,
        )
          ? current
              .editor_schema
              .blocks
          : normalizedSchema.blocks;

      const block =
        makeControlled2DEditorBlock(
          type,
          t,
        );

      return {
        ...current,

        editor_schema: {
          ...normalizedSchema,

          ...(current
            .editor_schema
            ?.canvas
            ? {
                canvas:
                  current
                    .editor_schema
                    .canvas,
              }
            : {}),

          blocks: [
            ...schemaBlocks,
            block,
          ],
        },
      };
    });
  };

  const removeEditorBlock = (
    blockId,
  ) => {
    setForm((current) => {
      const normalizedSchema =
        normalizeQrEditorSchema(
          current.editor_schema,
        );

      const schemaBlocks =
        Array.isArray(
          current
            .editor_schema
            ?.blocks,
        )
          ? current
              .editor_schema
              .blocks
          : normalizedSchema.blocks;

      return {
        ...current,

        editor_schema: {
          ...normalizedSchema,

          ...(current
            .editor_schema
            ?.canvas
            ? {
                canvas:
                  current
                    .editor_schema
                    .canvas,
              }
            : {}),

          blocks:
            schemaBlocks.filter(
              (block) =>
                block.id !==
                blockId,
            ),
        },
      };
    });
  };

  const updateOption = (
    key,
    index,
    field,
    value,
  ) => {
    setForm(
      (current) => ({
        ...current,

        [key]:
          current[key].map(
            (
              item,
              itemIndex,
            ) =>
              itemIndex ===
              index
                ? {
                    ...item,
                    [field]:
                      value,
                  }
                : item,
          ),
      }),
    );
  };

  const removeOption = (
    key,
    index,
  ) => {
    setForm(
      (current) => ({
        ...current,

        [key]:
          current[
            key
          ].filter(
            (
              _,
              itemIndex,
            ) =>
              itemIndex !==
              index,
          ),
      }),
    );
  };

  const addOption = (
    key,
    option,
  ) => {
    setForm(
      (current) => ({
        ...current,

        [key]: [
          ...current[key],
          option,
        ],
      }),
    );
  };

  const productTypeOptions =
    useMemo(
      () =>
        PRODUCT_TYPES.map(
          ([
            value,
            fallback,
          ]) => ({
            value,

            label:
              translateFallback(
                t,
                `qrPrint.types.${value}`,
                fallback,
              ),
          }),
        ),

      [t],
    );

  const mainImageName =
    getFileName(
      form.main_image,
    );

  const galleryCount =
    form.gallery_images
      ?.length || 0;

  const tagsText =
    useMemo(
      () =>
        (
          form.recommendation_tags ||
          []
        ).join(", "),

      [
        form.recommendation_tags,
      ],
    );

  const submit = async (
    event,
  ) => {
    event.preventDefault();

    setSaving(true);

    try {
      const payload = {
        ...form,

        base_unit_price:
          form.base_unit_price ===
          ""
            ? null
            : Number(
                form.base_unit_price,
              ),

        minimum_quantity:
          Number(
            form.minimum_quantity ||
              1,
          ),

        sort_order:
          Number(
            form.sort_order ||
              0,
          ),

        available_sizes:
          form.available_sizes.filter(
            (item) =>
              item.value ||
              item.label,
          ),

        available_materials:
          form.available_materials.filter(
            (item) =>
              item.value ||
              item.label,
          ),

        available_colors:
          form.available_colors.filter(
            (item) =>
              item.value ||
              item.label,
          ),
      };

      if (editing) {
        await qrPrintApi.updateProduct(
          productId,
          payload,
        );
      } else {
        await qrPrintApi.createProduct(
          payload,
        );
      }

      navigate(
        "/qr-print/products",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main
        className={`qr-print-product-form-page ${
          isRtl
            ? "qr-print-product-form-page--rtl"
            : "qr-print-product-form-page--ltr"
        }`}
        dir={direction}
      >
        <section className="qr-print-form-loading-panel">
          <div className="qr-print-form-loading">
            <Loader2
              size={28}
            />

            <span>
              {translateFallback(
                t,
                "qrPrint.productForm.loading",
                "Loading product...",
              )}
            </span>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main
      className={`qr-print-product-form-page ${
        isRtl
          ? "qr-print-product-form-page--rtl"
          : "qr-print-product-form-page--ltr"
      }`}
      dir={direction}
    >
      <header className="qr-print-form-header">
        <button
          type="button"
          className="qr-print-form-back-button"
          onClick={() =>
            navigate(
              "/qr-print/products",
            )
          }
        >
          {isRtl ? (
            <ArrowRight
              size={18}
            />
          ) : (
            <ArrowLeft
              size={18}
            />
          )}

          <span>
            {translateFallback(
              t,
              "common.back",
              "Back",
            )}
          </span>
        </button>

        <div className="qr-print-form-header__copy">
          <p className="qr-print-form-eyebrow">
            {translateFallback(
              t,
              "qrPrint.productForm.eyebrow",
              "QR print products",
            )}
          </p>

          <h1>
            {editing
              ? translateFallback(
                  t,
                  "qrPrint.productForm.editTitle",
                  "Edit print product",
                )
              : translateFallback(
                  t,
                  "qrPrint.productForm.createTitle",
                  "Add print product",
                )}
          </h1>

          <span>
            {translateFallback(
              t,
              "qrPrint.productForm.pageDescription",
              "Configure product information, smart access, physical options and the 2D / 3D design.",
            )}
          </span>
        </div>
      </header>

      <form
        className="qr-print-form-shell"
        onSubmit={submit}
      >
        <section className="qr-print-form-section qr-print-form-section--basic">
          <SectionTitle
            icon={
              PackageOpen
            }
            title={translateFallback(
              t,
              "qrPrint.productForm.basicInfo",
              "Product information",
            )}
            description={translateFallback(
              t,
              "qrPrint.productForm.basicInfoDescription",
              "Configure the product name, type, price, quantity and description.",
            )}
          />

          <div className="qr-print-form-grid">
            <label>
              <span>
                {translateFallback(
                  t,
                  "qrPrint.productForm.name",
                  "Name",
                )}
              </span>

              <input
                required
                value={form.name}
                onChange={(
                  event,
                ) =>
                  update(
                    "name",
                    event.target
                      .value,
                  )
                }
              />
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  "qrPrint.productForm.productType",
                  "Product type",
                )}
              </span>

              <QrPrintFormSelect
                value={
                  form.product_type
                }
                options={
                  productTypeOptions
                }
                onChange={(
                  value,
                ) =>
                  update(
                    "product_type",
                    value,
                  )
                }
                label={translateFallback(
                  t,
                  "qrPrint.productForm.productType",
                  "Product type",
                )}
              />
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  "qrPrint.productForm.baseUnitPrice",
                  "Base unit price",
                )}
              </span>

              <input
                type="number"
                min="0"
                step="0.01"
                dir="ltr"
                value={
                  form.base_unit_price
                }
                onChange={(
                  event,
                ) =>
                  update(
                    "base_unit_price",
                    event.target
                      .value,
                  )
                }
              />
            </label>

            <label>
              <span>
                {translateFallback(
                  t,
                  "qrPrint.productForm.minimumQuantity",
                  "Minimum quantity",
                )}
              </span>

              <input
                type="number"
                min="1"
                dir="ltr"
                value={
                  form.minimum_quantity
                }
                onChange={(
                  event,
                ) =>
                  update(
                    "minimum_quantity",
                    event.target
                      .value,
                  )
                }
              />
            </label>

            <label className="qr-print-form-grid__full">
              <span>
                {translateFallback(
                  t,
                  "qrPrint.productForm.description",
                  "Description",
                )}
              </span>

              <textarea
                rows="4"
                value={
                  form.description
                }
                onChange={(
                  event,
                ) =>
                  update(
                    "description",
                    event.target
                      .value,
                  )
                }
              />
            </label>

            <label className="qr-print-form-grid__full">
              <span>
                {translateFallback(
                  t,
                  "qrPrint.productForm.recommendationTags",
                  "Recommendation tags",
                )}
              </span>

              <input
                dir="ltr"
                value={tagsText}
                placeholder={translateFallback(
                  t,
                  "qrPrint.productForm.recommendationTagsPlaceholder",
                  "restaurant, cafe, premium",
                )}
                onChange={(
                  event,
                ) =>
                  update(
                    "recommendation_tags",

                    event.target.value
                      .split(",")
                      .map(
                        (tag) =>
                          tag.trim(),
                      )
                      .filter(
                        Boolean,
                      ),
                  )
                }
              />
            </label>
          </div>
        </section>

        <section className="qr-print-form-section qr-print-form-section--access">
          <SectionTitle
            icon={Nfc}
            title={translateFallback(
              t,
              "qrPrint.productForm.smartAccess.title",
              "Smart access technology",
            )}
            description={translateFallback(
              t,
              "qrPrint.productForm.smartAccess.description",
              "Choose how customers open the menu and whether the product supports one or two printed sides.",
            )}
          />

          <div className="qr-print-access-options">
            {[
              [
                "qr",
                QrCode,
                translateFallback(
                  t,
                  "qrPrint.catalog.technologyQr",
                  "QR code",
                ),
                translateFallback(
                  t,
                  "qrPrint.productForm.smartAccess.qrDescription",
                  "Customers scan the printed QR code.",
                ),
              ],
              [
                "nfc",
                Nfc,
                translateFallback(
                  t,
                  "qrPrint.catalog.technologyNfc",
                  "NFC",
                ),
                translateFallback(
                  t,
                  "qrPrint.productForm.smartAccess.nfcDescription",
                  "Customers tap a compatible phone on the product.",
                ),
              ],
              [
                "qr_nfc",
                PanelsTopLeft,
                translateFallback(
                  t,
                  "qrPrint.catalog.technologyQrNfc",
                  "QR + NFC",
                ),
                translateFallback(
                  t,
                  "qrPrint.productForm.smartAccess.qrNfcDescription",
                  "Customers can either scan or tap.",
                ),
              ],
            ].map(
              ([
                value,
                Icon,
                label,
                description,
              ]) => (
                <button
                  key={value}
                  type="button"
                  className={`qr-print-access-card ${
                    form.access_type ===
                    value
                      ? "active"
                      : ""
                  }`}
                  onClick={() =>
                    updateAccessType(
                      value,
                    )
                  }
                >
                  <span className="qr-print-access-card__icon">
                    <Icon
                      size={25}
                    />
                  </span>

                  <span className="qr-print-access-card__copy">
                    <strong>
                      {label}
                    </strong>

                    <small>
                      {description}
                    </small>
                  </span>

                  <span className="qr-print-access-card__check">
                    {form.access_type ===
                      value && (
                      <Check
                        size={16}
                      />
                    )}
                  </span>
                </button>
              ),
            )}
          </div>

          <div className="qr-print-side-options">
            <div>
              <strong>
                {translateFallback(
                  t,
                  "qrPrint.productForm.designSides.title",
                  "Design sides",
                )}
              </strong>

              <span>
                {translateFallback(
                  t,
                  "qrPrint.productForm.designSides.description",
                  "Front is always available. Enable the back for double-sided printing and customization.",
                )}
              </span>
            </div>

            <button
              type="button"
              className={
                !form.supports_back_design
                  ? "is-active"
                  : ""
              }
              onClick={() =>
                setForm(
                  (current) => ({
                    ...current,

                    supports_back_design:
                      false,

                    editor_schema: {
                      ...normalizeQrEditorSchema(
                        current.editor_schema,
                      ),

                      sides: {
                        front: {
                          enabled:
                            true,
                          label:
                            translateFallback(
                              t,
                              "qrPrint.productForm.designSides.front",
                              "Front",
                            ),
                        },

                        back: {
                          enabled:
                            false,
                          label:
                            translateFallback(
                              t,
                              "qrPrint.productForm.designSides.back",
                              "Back",
                            ),
                        },
                      },
                    },
                  }),
                )
              }
            >
              {translateFallback(
                t,
                "qrPrint.productForm.designSides.frontOnly",
                "Front only",
              )}
            </button>

            <button
              type="button"
              className={
                form.supports_back_design
                  ? "is-active"
                  : ""
              }
              onClick={() =>
                setForm(
                  (current) => ({
                    ...current,

                    supports_back_design:
                      true,

                    editor_schema: {
                      ...normalizeQrEditorSchema(
                        current.editor_schema,
                      ),

                      sides: {
                        front: {
                          enabled:
                            true,
                          label:
                            translateFallback(
                              t,
                              "qrPrint.productForm.designSides.front",
                              "Front",
                            ),
                        },

                        back: {
                          enabled:
                            true,
                          label:
                            translateFallback(
                              t,
                              "qrPrint.productForm.designSides.back",
                              "Back",
                            ),
                        },
                      },
                    },
                  }),
                )
              }
            >
              {translateFallback(
                t,
                "qrPrint.productForm.designSides.frontBack",
                "Front + back",
              )}
            </button>
          </div>

          {form.access_type !==
          "qr" ? (
            <div className="qr-print-nfc-settings">
              <label>
                <span>
                  {translateFallback(
                    t,
                    "qrPrint.productForm.nfc.chipType",
                    "NFC chip type",
                  )}
                </span>

                <QrPrintFormSelect
                  value={
                    form.nfc_chip_type ||
                    "NTAG213"
                  }
                  options={[
                    {
                      value:
                        "NTAG213",
                      label:
                        "NTAG213",
                    },
                    {
                      value:
                        "NTAG215",
                      label:
                        "NTAG215",
                    },
                    {
                      value:
                        "NTAG216",
                      label:
                        "NTAG216",
                    },
                    {
                      value:
                        "other",
                      label:
                        translateFallback(
                          t,
                          "qrPrint.types.other",
                          "Other",
                        ),
                    },
                  ]}
                  onChange={(
                    value,
                  ) =>
                    update(
                      "nfc_chip_type",
                      value,
                    )
                  }
                  label={translateFallback(
                    t,
                    "qrPrint.productForm.nfc.chipType",
                    "NFC chip type",
                  )}
                />
              </label>

              <label className="qr-print-toggle-row">
                <input
                  type="checkbox"
                  checked={
                    form.nfc_writable
                  }
                  onChange={(
                    event,
                  ) =>
                    update(
                      "nfc_writable",
                      event.target
                        .checked,
                    )
                  }
                />

                <span>
                  {translateFallback(
                    t,
                    "qrPrint.productForm.nfc.writable",
                    "Writable NFC chip",
                  )}
                </span>
              </label>

              <label className="qr-print-toggle-row">
                <input
                  type="checkbox"
                  checked={
                    form.nfc_lock_after_write
                  }
                  onChange={(
                    event,
                  ) =>
                    update(
                      "nfc_lock_after_write",
                      event.target
                        .checked,
                    )
                  }
                />

                <span>
                  {translateFallback(
                    t,
                    "qrPrint.productForm.nfc.lockAfterWrite",
                    "Lock chip after programming",
                  )}
                </span>
              </label>

              <label className="qr-print-form-grid__full">
                <span>
                  {translateFallback(
                    t,
                    "qrPrint.productForm.nfc.productionNotes",
                    "Production notes",
                  )}
                </span>

                <textarea
                  rows="3"
                  value={
                    form.nfc_notes
                  }
                  placeholder={translateFallback(
                    t,
                    "qrPrint.productForm.nfc.productionNotesPlaceholder",
                    "Optional internal NFC production instructions",
                  )}
                  onChange={(
                    event,
                  ) =>
                    update(
                      "nfc_notes",
                      event.target
                        .value,
                    )
                  }
                />
              </label>
            </div>
          ) : null}
        </section>

        <section className="qr-print-form-section qr-print-form-section--media">
          <SectionTitle
            icon={Images}
            title={translateFallback(
              t,
              "qrPrint.productForm.images",
              "Product images",
            )}
            description={translateFallback(
              t,
              "qrPrint.productForm.imagesDescription",
              "Upload the main product image and any additional gallery images.",
            )}
          />

          <div className="qr-print-file-grid">
            <label className="qr-print-file-box">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(
                  event,
                ) =>
                  update(
                    "main_image",
                    event.target
                      .files?.[0] ||
                      null,
                  )
                }
              />

              <span className="qr-print-file-box__preview">
                <ImagePlus
                  size={36}
                />
              </span>

              <strong>
                {translateFallback(
                  t,
                  "qrPrint.productForm.mainImage",
                  "Main image",
                )}
              </strong>

              <small>
                {mainImageName ||
                  translateFallback(
                    t,
                    "qrPrint.productForm.mainImageHint",
                    "PNG, JPG or WEBP",
                  )}
              </small>
            </label>

            <label className="qr-print-file-box">
              <input
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp"
                onChange={(
                  event,
                ) =>
                  update(
                    "gallery_images",
                    Array.from(
                      event.target
                        .files || [],
                    ),
                  )
                }
              />

              <span className="qr-print-file-box__preview">
                <Images
                  size={36}
                />
              </span>

              <strong>
                {translateFallback(
                  t,
                  "qrPrint.productForm.galleryImages",
                  "Gallery images",
                )}
              </strong>

              <small>
                {galleryCount
                  ? `${galleryCount} ${translateFallback(
                      t,
                      "qrPrint.productForm.filesSelected",
                      "files selected",
                    )}`
                  : translateFallback(
                      t,
                      "qrPrint.productForm.galleryHint",
                      "Add one or more images",
                    )}
              </small>
            </label>
          </div>

          {(form.saved_main_image_path && !form.remove_main_image) || form.main_image ? (
            <div className="qr-print-image-library-group">
              <strong>Main image preview</strong>
              <div className="qr-print-image-library">
                {form.main_image ? (
                  <div className="qr-print-image-item">
                    <NewProductImage file={form.main_image} alt="New main product image" />
                    <span>New main image</span>
                    <button type="button" className="qr-print-image-remove" onClick={() => update("main_image", null)}>
                      <Trash2 size={15} /> Remove
                    </button>
                  </div>
                ) : form.saved_main_image_path && !form.remove_main_image ? (
                  <div className="qr-print-image-item">
                    <SavedProductImage productId={productId} imageType="main" alt="Saved main product image" />
                    <span>Saved main image</span>
                    <button type="button" className="qr-print-image-remove" onClick={() => setForm(current => ({ ...current, remove_main_image: true }))}>
                      <Trash2 size={15} /> Delete
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {(form.saved_gallery?.length > 0 || form.gallery_images?.length > 0) ? (
            <div className="qr-print-image-library-group">
              <strong>Gallery images</strong>
              <div className="qr-print-image-library">
                {(form.saved_gallery || []).map((path, index) => (
                  (form.remove_gallery_images || []).includes(path) ? null : (
                    <div className="qr-print-image-item" key={`saved-${index}`}>
                      <SavedProductImage productId={productId} imageType="gallery" imageIndex={index} alt={`Gallery image ${index + 1}`} />
                      <span>Saved image {index + 1}</span>
                      <button type="button" className="qr-print-image-remove" onClick={() => setForm(current => ({
                        ...current,
                        remove_gallery_images: [...(current.remove_gallery_images || []), path],
                      }))}>
                        <Trash2 size={15} /> Delete
                      </button>
                    </div>
                  )
                ))}
                {(form.gallery_images || []).map((file, index) => (
                  <div className="qr-print-image-item" key={`new-${index}-${file.name}`}>
                    <NewProductImage file={file} alt={`New gallery image ${index + 1}`} />
                    <span title={file.name}>{file.name}</span>
                    <button type="button" className="qr-print-image-remove" onClick={() => setForm(current => ({
                      ...current,
                      gallery_images: current.gallery_images.filter((_, i) => i !== index),
                    }))}>
                      <Trash2 size={15} /> Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {(form.remove_main_image || form.remove_gallery_images?.length > 0) && (
            <p className="qr-print-image-save-note">Image deletions are saved when you click Save product.</p>
          )}
        </section>

        <section className="qr-print-form-section qr-print-form-section--blender">
          <SectionTitle
            icon={FileBox}
            title="3D model (GLB)"
            description="Export your design from Blender as GLB and upload it for an interactive 3D preview."
          />
          <div className="qr-print-blender-upload">
            <label className="qr-print-blender-upload__dropzone">
              <input
                type="file"
                accept=".glb,model/gltf-binary"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  if (!file) return;
                  if (!/\.glb$/i.test(file.name) || file.size > 50 * 1024 * 1024 || file.size < 20) {
                    setGlbUploadError('Choose a valid .glb file up to 50 MB.');
                    event.target.value = '';
                    return;
                  }
                  setGlbUploadError('');
                  setForm(current => ({
                    ...current,
                    three_d_model: file,
                    three_d_source: 'model',
                    preview_mode: current.preview_mode === '2d' ? 'both' : current.preview_mode,
                    remove_three_d_model: false,
                  }));
                }}
              />
              <FileBox size={25} />
              <strong>{form.three_d_model ? "Replace GLB model" : "Choose GLB model"}</strong>
              <small>.glb only · up to 50 MB</small>
            </label>
            <div className="qr-print-blender-upload__info">
              <strong>{form.three_d_model?.name || (form.three_d_model_url ? "An existing 3D model is attached" : "No design file selected")}</strong>
              <p>GLB files are ready for browser 3D preview. In Blender use File → Export → glTF 2.0 (.glb).</p>
              {(form.three_d_model || form.three_d_model_url) && (
                <button
                  type="button"
                  className="qr-print-blender-upload__remove"
                  onClick={() => setForm((current) => ({
                    ...current,
                    three_d_model: null,
                    three_d_model_url: null,
                    remove_three_d_model: true,
                  }))}
                >
                  <Trash2 size={15} /> Remove 3D file
                </button>
              )}
            </div>
          </div>
          {glbUploadError && <p className="qr-print-blender-upload__error" role="alert">{glbUploadError}</p>}
          <p className="qr-print-blender-upload__note">GLB models upload directly using the existing three_d_model API field. No conversion worker is required.</p>

        </section>

        <section className="qr-print-form-section qr-print-form-section--preview">
          <SectionTitle
            icon={Box}
            title={translateFallback(
              t,
              "qrPrint.productForm.previewSupport.title",
              "Preview support",
            )}
            description={translateFallback(
              t,
              "qrPrint.productForm.previewSupport.description",
              "Choose whether restaurant owners can customize this product in 2D, 3D, or both.",
            )}
          />

          <div className="qr-print-preview-support">
            {[
              [
                "2d",
                translateFallback(
                  t,
                  "qrPrint.productForm.previewMode2d",
                  "2D",
                ),
              ],
              [
                "3d",
                translateFallback(
                  t,
                  "qrPrint.productForm.previewMode3d",
                  "3D",
                ),
              ],
              [
                "both",
                translateFallback(
                  t,
                  "qrPrint.productForm.previewModeBoth",
                  "2D and 3D",
                ),
              ],
            ].map(
              ([
                value,
                label,
              ]) => (
                <button
                  key={value}
                  type="button"
                  className={
                    form.preview_mode ===
                    value
                      ? "is-active"
                      : ""
                  }
                  onClick={() =>
                    update(
                      "preview_mode",
                      value,
                    )
                  }
                >
                  {label}
                </button>
              ),
            )}
          </div>
        </section>

        <QrPrintDesignStudio
          productId={editing ? productId : null}
          form={form}
          update={update}
          productName={
            form.name
          }
          productType={
            form.product_type
          }
          accessType={
            form.access_type
          }
          supportsBackDesign={
            form.supports_back_design
          }
          editorSchema={
            form.editor_schema
          }
          onBlockChange={
            updateEditorBlockDefault
          }
          onBlockValueChange={
            replaceEditorBlockDefault
          }
          onAddBlock={
            addEditorBlock
          }
          onRemoveBlock={
            removeEditorBlock
          }
          onCanvasSizeChange={
            updateEditorCanvasSize
          }
        />

        <OptionSection
          icon={Settings2}
          title={translateFallback(
            t,
            "qrPrint.productForm.sizes",
            "Sizes",
          )}
          description={translateFallback(
            t,
            "qrPrint.productForm.sizesDescription",
            "Add the size choices available for this physical product.",
          )}
          items={
            form.available_sizes
          }
          onAdd={() =>
            addOption(
              "available_sizes",
              makeOption(),
            )
          }
          onChange={(
            index,
            field,
            value,
          ) =>
            updateOption(
              "available_sizes",
              index,
              field,
              value,
            )
          }
          onRemove={(
            index,
          ) =>
            removeOption(
              "available_sizes",
              index,
            )
          }
        />

        <OptionSection
          icon={PackageOpen}
          title={translateFallback(
            t,
            "qrPrint.productForm.materials",
            "Materials",
          )}
          description={translateFallback(
            t,
            "qrPrint.productForm.materialsDescription",
            "Add the materials customers can choose for this product.",
          )}
          items={
            form.available_materials
          }
          onAdd={() =>
            addOption(
              "available_materials",
              makeOption(),
            )
          }
          onChange={(
            index,
            field,
            value,
          ) =>
            updateOption(
              "available_materials",
              index,
              field,
              value,
            )
          }
          onRemove={(
            index,
          ) =>
            removeOption(
              "available_materials",
              index,
            )
          }
        />

        <section className="qr-print-form-section qr-print-form-section--colors">
          <SectionTitle
            icon={Palette}
            title={translateFallback(
              t,
              "qrPrint.productForm.colors",
              "Available colors",
            )}
            description={translateFallback(
              t,
              "qrPrint.productForm.colorsDescription",
              "Add the color variants that can be selected for this product.",
            )}
            action={
              <button
                type="button"
                className="qr-print-icon-button"
                onClick={() =>
                  addOption(
                    "available_colors",
                    makeColor(),
                  )
                }
              >
                <Plus
                  size={18}
                />
              </button>
            }
          />

          <div className="qr-print-option-list">
            {form.available_colors.map(
              (
                option,
                index,
              ) => (
                <div
                  className="qr-print-option-row qr-print-option-row--color"
                  key={`color-${index}`}
                >
                  <input
                    dir="ltr"
                    value={
                      option.value ||
                      ""
                    }
                    placeholder={translateFallback(
                      t,
                      "qrPrint.productForm.optionValue",
                      "Value",
                    )}
                    onChange={(
                      event,
                    ) =>
                      updateOption(
                        "available_colors",
                        index,
                        "value",
                        event
                          .target
                          .value,
                      )
                    }
                  />

                  <input
                    value={
                      option.label ||
                      ""
                    }
                    placeholder={translateFallback(
                      t,
                      "qrPrint.productForm.optionLabel",
                      "Label",
                    )}
                    onChange={(
                      event,
                    ) =>
                      updateOption(
                        "available_colors",
                        index,
                        "label",
                        event
                          .target
                          .value,
                      )
                    }
                  />

                  <input
                    type="color"
                    value={
                      option.hex ||
                      "#ffffff"
                    }
                    onChange={(
                      event,
                    ) =>
                      updateOption(
                        "available_colors",
                        index,
                        "hex",
                        event
                          .target
                          .value,
                      )
                    }
                  />

                  <button
                    type="button"
                    className="qr-print-icon-button is-danger"
                    aria-label="Remove option"
                    title="Remove option"
                    onClick={() =>
                      removeOption(
                        "available_colors",
                        index,
                      )
                    }
                  >
                    <Trash2
                      size={17}
                    />
                  </button>
                </div>
              ),
            )}
          </div>
        </section>

        <section className="qr-print-form-section qr-print-form-section--availability">
          <SectionTitle
            icon={Settings2}
            title={translateFallback(
              t,
              "qrPrint.productForm.availabilityTitle",
              "Product availability",
            )}
            description={translateFallback(
              t,
              "qrPrint.productForm.availabilityDescription",
              "Control whether this product is active and whether it should be recommended.",
            )}
          />

          <div className="qr-print-toggle-grid">
            <label className="qr-print-toggle-row">
              <input
                type="checkbox"
                checked={
                  form.is_active
                }
                onChange={(
                  event,
                ) =>
                  update(
                    "is_active",
                    event.target
                      .checked,
                  )
                }
              />

              <span>
                {translateFallback(
                  t,
                  "qrPrint.productForm.isActive",
                  "Active product",
                )}
              </span>
            </label>

            <label className="qr-print-toggle-row">
              <input
                type="checkbox"
                checked={
                  form.is_recommended
                }
                onChange={(
                  event,
                ) =>
                  update(
                    "is_recommended",
                    event.target
                      .checked,
                  )
                }
              />

              <span>
                {translateFallback(
                  t,
                  "qrPrint.productForm.isRecommended",
                  "Recommended product",
                )}
              </span>
            </label>
          </div>
        </section>

        <div className="qr-print-form-actions">
          <Link
            to="/qr-print/products"
            className="qr-print-form-cancel-button"
          >
            {translateFallback(
              t,
              "common.cancel",
              "Cancel",
            )}
          </Link>

          <button
            type="submit"
            disabled={saving}
            className="qr-print-form-save-button"
          >
            {saving ? (
              <Loader2
                size={18}
                className="qr-print-form-spinner"
              />
            ) : (
              <Save
                size={18}
              />
            )}

            <span>
              {saving
                ? translateFallback(
                    t,
                    "common.saving",
                    "Saving...",
                  )
                : translateFallback(
                    t,
                    "qrPrint.productForm.save",
                    "Save product",
                  )}
            </span>
          </button>
        </div>
      </form>
    </main>
  );
}

function QrPrintDesignStudio({
  productId,
  form,
  update,
  productName,
  productType,
  accessType,
  supportsBackDesign,
  editorSchema,
  onBlockChange,
  onBlockValueChange,
  onAddBlock,
  onRemoveBlock,
  onCanvasSizeChange,
}) {
  const { t } =
    useLanguage();

  const [
    activeView,
    setActiveView,
  ] = useState(
    form.preview_mode ===
      "3d"
      ? "3d"
      : "2d",
  );

  const [
    selectedBlockId,
    setSelectedBlockId,
  ] = useState(null);

  const [
    previewZoom,
    setPreviewZoom,
  ] = useState(100);

  const normalizedSchema =
    normalizeQrEditorSchema(
      editorSchema,
    );

  const blocks =
    normalizedSchema.blocks ||
    [];

  const printSizeCm =
    normalizePrintSizeCm(
      editorSchema,
      productType,
    );

  const selectedBlock =
    blocks.find(
      (block) =>
        block.id ===
        selectedBlockId,
    ) ||
    blocks.find((block) =>
      [
        "text",
        "logo",
        "image",
        "qr",
        "nfc",
        "shape",
      ].includes(
        block.type,
      ),
    ) ||
    null;

  useEffect(() => {
    if (
      form.preview_mode ===
      "2d"
    ) {
      setActiveView(
        "2d",
      );

      return;
    }

    if (
      form.preview_mode ===
      "3d"
    ) {
      setActiveView(
        "3d",
      );
    }
  }, [form.preview_mode]);

  // An uploaded GLB belongs in the existing 3D design studio, not a second viewer.
  useEffect(() => {
    if (form.three_d_model) setActiveView("3d");
  }, [form.three_d_model]);

  useEffect(() => {
    if (
      !selectedBlockId &&
      selectedBlock
    ) {
      setSelectedBlockId(
        selectedBlock.id,
      );

      return;
    }

    if (
      selectedBlockId &&
      !blocks.some(
        (block) =>
          block.id ===
          selectedBlockId,
      )
    ) {
      setSelectedBlockId(
        blocks[0]?.id ||
          null,
      );
    }
  }, [
    blocks,
    selectedBlock,
    selectedBlockId,
  ]);

  useEffect(() => {
    const handleDeleteSelectedLayer =
      (event) => {
        if (
          event.key !==
            "Delete" &&
          event.key !==
            "Backspace"
        ) {
          return;
        }

        if (
          event.ctrlKey ||
          event.metaKey ||
          event.altKey
        ) {
          return;
        }

        const tagName =
          event.target?.tagName?.toLowerCase();

        const isTypingTarget =
          [
            "input",
            "textarea",
            "select",
          ].includes(
            tagName,
          ) ||
          event.target
            ?.isContentEditable;

        if (
          isTypingTarget
        ) {
          return;
        }

        const canDeleteSelectedBlock =
          selectedBlock &&
          (!selectedBlock.required ||
            [
              "shape",
              "image",
              "nfc",
            ].includes(
              selectedBlock.type,
            ));

        if (
          !canDeleteSelectedBlock
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        onRemoveBlock(
          selectedBlock.id,
        );

        setSelectedBlockId(
          null,
        );
      };

    document.addEventListener(
      "keydown",
      handleDeleteSelectedLayer,
      true,
    );

    return () => {
      document.removeEventListener(
        "keydown",
        handleDeleteSelectedLayer,
        true,
      );
    };
  }, [
    selectedBlock,
    onRemoveBlock,
  ]);

  const showSwitcher =
    form.preview_mode ===
    "both";

  const canShow2D =
    form.preview_mode !==
    "3d";

  const canShow3D =
    form.preview_mode !==
    "2d";

  const currentView =
    activeView === "3d" &&
    canShow3D
      ? "3d"
      : "2d";

  return (
    <section className="qr-print-form-section qr-print-form-section--designer qr-print-design-studio-card">
      <SectionTitle
        icon={Palette}
        title={translateFallback(
          t,
          "qrPrint.productForm.designStudio.title",
          "Product design studio",
        )}
        description={translateFallback(
          t,
          "qrPrint.productForm.designStudio.description",
          "Edit the real product design with 2D controls or configure the 3D model.",
        )}
        action={
          showSwitcher ? (
            <div className="qr-print-design-view-switcher">
              <button
                type="button"
                className={
                  currentView ===
                  "2d"
                    ? "is-active"
                    : ""
                }
                onClick={() =>
                  setActiveView(
                    "2d",
                  )
                }
              >
                {translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.design2d",
                  "2D design",
                )}
              </button>

              <button
                type="button"
                className={
                  currentView ===
                  "3d"
                    ? "is-active"
                    : ""
                }
                onClick={() =>
                  setActiveView(
                    "3d",
                  )
                }
              >
                {translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.model3d",
                  "3D model",
                )}
              </button>
            </div>
          ) : null
        }
      />

      {currentView ===
        "2d" &&
      canShow2D ? (
        <div className="qr-print-design-studio-layout">
          <QrPrint2DControlsSidebar
            blocks={blocks}
            selectedBlock={
              selectedBlock
            }
            selectedBlockId={
              selectedBlockId
            }
            onSelectBlock={
              setSelectedBlockId
            }
            onBlockChange={
              onBlockChange
            }
            onBlockValueChange={
              onBlockValueChange
            }
            onAddBlock={
              onAddBlock
            }
            onRemoveBlock={
              onRemoveBlock
            }
            printSizeCm={
              printSizeCm
            }
            onCanvasSizeChange={
              onCanvasSizeChange
            }
            previewZoom={
              previewZoom
            }
            onPreviewZoomChange={
              setPreviewZoom
            }
          />

          <QrPrint2DProductPreview
            productName={
              productName
            }
            productType={
              productType
            }
            accessType={
              accessType
            }
            supportsBackDesign={
              supportsBackDesign
            }
            editorSchema={
              editorSchema
            }
            selectedBlockId={
              selectedBlockId
            }
            onSelectBlock={
              setSelectedBlockId
            }
            onBlockChange={
              onBlockChange
            }
            printSizeCm={
              printSizeCm
            }
            previewZoom={
              previewZoom
            }
          />
        </div>
      ) : null}

      {currentView ===
        "3d" &&
      canShow3D ? (
        <div className="qr-print-design-studio-3d">
          <QrPrint3DProductEditor
            modelPreview={(form.three_d_model || form.three_d_model_url) ? (
              <LocalGlbPreview
                file={form.three_d_model}
                existingUrl={form.three_d_model_url}
                productId={productId}
              />
            ) : null}
            value={
              form.three_d_definition
            }
            onChange={(
              value,
            ) =>
              update(
                "three_d_definition",
                value,
              )
            }
            source={
              form.three_d_source
            }
            onSourceChange={(
              value,
            ) =>
              update(
                "three_d_source",
                value,
              )
            }
            modelFile={
              form.three_d_model
            }
            modelUrl={
              form.three_d_model_url
            }
            onModelFileChange={(
              file,
            ) => {
              update(
                "three_d_model",
                file,
              );

              update(
                "remove_three_d_model",
                false,
              );
            }}
            onRemoveModel={() => {
              update(
                "three_d_model",
                null,
              );

              update(
                "three_d_model_url",
                null,
              );

              update(
                "remove_three_d_model",
                true,
              );
            }}
          />
        </div>
      ) : null}
    </section>
  );
}

function QrPrint2DControlsSidebar({
  blocks,
  selectedBlock,
  selectedBlockId,
  onSelectBlock,
  onBlockChange,
  onBlockValueChange,
  onAddBlock,
  onRemoveBlock,
  printSizeCm,
  onCanvasSizeChange,
  previewZoom,
  onPreviewZoomChange,
}) {
  const { t } =
    useLanguage();

  const editableBlocks =
    blocks.filter(
      (block) =>
        [
          "text",
          "logo",
          "image",
          "qr",
          "nfc",
          "shape",
          "background_color",
          "product_color",
        ].includes(
          block.type,
        ),
    );

  const selectedValue =
    selectedBlock
      ? getSchemaElementValue(
          selectedBlock,
        )
      : {};

  const updateSelected = (
    patch,
  ) => {
    if (!selectedBlock) {
      return;
    }

    onBlockChange(
      selectedBlock.id,
      patch,
    );
  };

  const replaceSelected = (
    value,
  ) => {
    if (!selectedBlock) {
      return;
    }

    onBlockValueChange(
      selectedBlock.id,
      value,
    );
  };

  const blockIcon = (
    type,
  ) => {
    if (type === "text") {
      return (
        <Type size={15} />
      );
    }

    if (type === "qr") {
      return (
        <QrCode
          size={15}
        />
      );
    }

    if (type === "nfc") {
      return (
        <Nfc size={15} />
      );
    }

    if (
      type === "shape"
    ) {
      return (
        <Square
          size={15}
        />
      );
    }

    return (
      <ImagePlus
        size={15}
      />
    );
  };

  return (
    <aside className="qr-print-design-sidebar">
      <div className="qr-print-design-sidebar__section">
        <strong className="qr-print-design-sidebar__title">
          {translateFallback(
            t,
            "qrPrint.productForm.designStudio.printBackgroundSize",
            "Print background size",
          )}
        </strong>

        <p className="qr-print-design-sidebar__hint">
          {translateFallback(
            t,
            "qrPrint.productForm.designStudio.printBackgroundSizeHint",
            "Set the real width and height in centimeters.",
          )}
        </p>

        <div className="qr-print-design-control-grid two-cols">
          <DesignNumberControl
            label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.width",
                  "Width",
                )}
            value={
              printSizeCm.width
            }
            min={
              PRINT_SIZE_MIN_CM
            }
            max={
              PRINT_SIZE_MAX_CM
            }
            step={0.01}
            suffix="cm"
            onChange={(
              value,
            ) =>
              onCanvasSizeChange(
                {
                  width_cm:
                    value,
                },
              )
            }
          />

          <DesignNumberControl
            label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.height",
                  "Height",
                )}
            value={
              printSizeCm.height
            }
            min={
              PRINT_SIZE_MIN_CM
            }
            max={
              PRINT_SIZE_MAX_CM
            }
            step={0.01}
            suffix="cm"
            onChange={(
              value,
            ) =>
              onCanvasSizeChange(
                {
                  height_cm:
                    value,
                },
              )
            }
          />
        </div>
      </div>

      <div className="qr-print-design-sidebar__section">
        <strong className="qr-print-design-sidebar__title">
          {translateFallback(
            t,
            "qrPrint.productForm.designStudio.previewZoom",
            "Preview zoom",
          )}
        </strong>

        <p className="qr-print-design-sidebar__hint">
          {translateFallback(
            t,
            "qrPrint.productForm.designStudio.previewZoomHint",
            "Zoom changes only the screen preview.",
          )}
        </p>

        <DesignNumberControl
          label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.zoom",
                  "Zoom",
                )}
          value={
            previewZoom
          }
          min={40}
          max={220}
          step={5}
          suffix="%"
          onChange={
            onPreviewZoomChange
          }
        />
      </div>

      <div className="qr-print-design-sidebar__section">
        <strong className="qr-print-design-sidebar__title">
          {translateFallback(
            t,
            "qrPrint.productForm.designStudio.addElements",
            "Add elements",
          )}
        </strong>

        <div className="qr-print-design-add-grid">
          <button
            type="button"
            onClick={() =>
              onAddBlock(
                "text",
              )
            }
          >
            <Type size={15} />
            {translateFallback(
              t,
              "qrPrint.productForm.designStudio.text",
              "Text",
            )}
          </button>

          <button
            type="button"
            onClick={() =>
              onAddBlock(
                "qr",
              )
            }
          >
            <QrCode
              size={15}
            />
            {translateFallback(
              t,
              "qrPrint.productForm.designStudio.qr",
              "QR",
            )}
          </button>

          <button
            type="button"
            onClick={() =>
              onAddBlock(
                "nfc",
              )
            }
          >
            <Nfc size={15} />
            {translateFallback(
              t,
              "qrPrint.productForm.designStudio.nfc",
              "NFC",
            )}
          </button>

          <button
            type="button"
            onClick={() =>
              onAddBlock(
                "shape",
              )
            }
          >
            <Square
              size={15}
            />
            {translateFallback(
              t,
              "qrPrint.productForm.designStudio.shape",
              "Shape",
            )}
          </button>
        </div>
      </div>

      <div className="qr-print-design-sidebar__section">
        <strong className="qr-print-design-sidebar__title">
          {translateFallback(
            t,
            "qrPrint.productForm.designStudio.layers",
            "Layers",
          )}
        </strong>

        <div className="qr-print-design-layers">
          {editableBlocks.map(
            (block) => (
              <button
                key={
                  block.id
                }
                type="button"
                className={
                  selectedBlockId ===
                  block.id
                    ? "is-active"
                    : ""
                }
                onClick={() =>
                  onSelectBlock(
                    block.id,
                  )
                }
              >
                {blockIcon(
                  block.type,
                )}

                <span>
                  {getEditorBlockDisplayLabel(
                    t,
                    block,
                  )}
                </span>
              </button>
            ),
          )}
        </div>
      </div>

      {selectedBlock ? (
        <div className="qr-print-design-sidebar__section">
          <div className="qr-print-design-sidebar__selected-title">
            <strong>
              {getEditorBlockDisplayLabel(
                t,
                selectedBlock,
              )}
            </strong>

            {!selectedBlock.required ||
            [
              "shape",
              "image",
              "nfc",
            ].includes(
              selectedBlock.type,
            ) ? (
              <button
                type="button"
                className="qr-print-design-remove-layer"
                onClick={() => {
                  onRemoveBlock(
                    selectedBlock.id,
                  );

                  onSelectBlock(
                    null,
                  );
                }}
              >
                <Trash2
                  size={14}
                />

                <span>
                  {translateFallback(
                    t,
                    "common.delete",
                    "Delete",
                  )}
                </span>
              </button>
            ) : null}
          </div>

          {selectedBlock.type ===
          "background_color" ? (
            <DesignControlRow
              label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.backgroundColor",
                  "Background color",
                )}
            >
              <input
                type="color"
                value={normalizeHexColor(
                  selectedBlock.default,
                  "#ffffff",
                )}
                onChange={(
                  event,
                ) =>
                  replaceSelected(
                    event.target
                      .value,
                  )
                }
              />
            </DesignControlRow>
          ) : null}

          {selectedBlock.type ===
          "product_color" ? (
            <DesignControlRow
              label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.productBorderColor",
                  "Product border color",
                )}
            >
              <input
                type="color"
                value={normalizeHexColor(
                  selectedBlock.default,
                  "#ffffff",
                )}
                onChange={(
                  event,
                ) =>
                  replaceSelected(
                    event.target
                      .value,
                  )
                }
              />
            </DesignControlRow>
          ) : null}

          {[
            "text",
            "logo",
            "image",
            "qr",
            "nfc",
            "shape",
          ].includes(
            selectedBlock.type,
          ) ? (
            <div className="qr-print-design-control-grid two-cols">
              <DesignNumberControl
                label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.x",
                  "X",
                )}
                value={
                  selectedValue.x ??
                  50
                }
                min={0}
                max={100}
                step={0.01}
                suffix="%"
                onChange={(
                  value,
                ) =>
                  updateSelected(
                    {
                      x: value,
                    },
                  )
                }
              />

              <DesignNumberControl
                label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.y",
                  "Y",
                )}
                value={
                  selectedValue.y ??
                  50
                }
                min={0}
                max={100}
                step={0.01}
                suffix="%"
                onChange={(
                  value,
                ) =>
                  updateSelected(
                    {
                      y: value,
                    },
                  )
                }
              />
            </div>
          ) : null}

          {selectedBlock.type ===
          "text" ? (
            <>
              <DesignControlRow label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.text",
                  "Text",
                )}>
                <textarea
                  rows="3"
                  value={
                    selectedValue.text ||
                    ""
                  }
                  placeholder={translateFallback(
                    t,
                    "qrPrint.productForm.designStudio.textPlaceholder",
                    "Write the displayed text",
                  )}
                  onChange={(
                    event,
                  ) =>
                    updateSelected(
                      {
                        text:
                          event
                            .target
                            .value,
                      },
                    )
                  }
                />
              </DesignControlRow>

              <div className="qr-print-design-control-grid two-cols">
                <DesignNumberControl
                  label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.size",
                  "Size",
                )}
                  value={
                    selectedValue.font_size ??
                    4
                  }
                  min={1.2}
                  max={12}
                  step={0.1}
                  onChange={(
                    value,
                  ) =>
                    updateSelected(
                      {
                        font_size:
                          value,
                      },
                    )
                  }
                />

                <DesignNumberControl
                  label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.weight",
                  "Weight",
                )}
                  value={
                    selectedValue.weight ??
                    700
                  }
                  min={300}
                  max={950}
                  step={50}
                  onChange={(
                    value,
                  ) =>
                    updateSelected(
                      {
                        weight:
                          value,
                      },
                    )
                  }
                />
              </div>

              <DesignControlRow label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.textColor",
                  "Text color",
                )}>
                <input
                  type="color"
                  value={normalizeHexColor(
                    selectedValue.color,
                    "#1f2937",
                  )}
                  onChange={(
                    event,
                  ) =>
                    updateSelected(
                      {
                        color:
                          event
                            .target
                            .value,
                      },
                    )
                  }
                />
              </DesignControlRow>

              <DesignControlRow label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.alignment",
                  "Alignment",
                )}>
                <QrPrintFormSelect
                  value={
                    selectedValue.align ||
                    "center"
                  }
                  options={[
                    {
                      value:
                        "left",
                      label:
                        translateFallback(
                          t,
                          "qrPrint.productForm.designStudio.left",
                          "Left",
                        ),
                    },
                    {
                      value:
                        "center",
                      label:
                        translateFallback(
                          t,
                          "qrPrint.productForm.designStudio.center",
                          "Center",
                        ),
                    },
                    {
                      value:
                        "right",
                      label:
                        translateFallback(
                          t,
                          "qrPrint.productForm.designStudio.right",
                          "Right",
                        ),
                    },
                  ]}
                  onChange={(
                    value,
                  ) =>
                    updateSelected(
                      {
                        align:
                          value,
                      },
                    )
                  }
                  label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.alignment",
                  "Alignment",
                )}
                />
              </DesignControlRow>
            </>
          ) : null}

          {selectedBlock.type ===
          "qr" ? (
            <>
              <DesignNumberControl
                label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.qrSize",
                  "QR size",
                )}
                value={
                  selectedValue.size ??
                  34
                }
                min={12}
                max={72}
                step={1}
                suffix="%"
                onChange={(
                  value,
                ) =>
                  updateSelected(
                    {
                      size:
                        value,
                    },
                  )
                }
              />

              <div className="qr-print-design-control-grid two-cols">
                <DesignControlRow label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.qrColor",
                  "QR color",
                )}>
                  <input
                    type="color"
                    value={normalizeHexColor(
                      selectedValue.foreground_color,
                      "#111111",
                    )}
                    onChange={(
                      event,
                    ) =>
                      updateSelected(
                        {
                          foreground_color:
                            event
                              .target
                              .value,
                        },
                      )
                    }
                  />
                </DesignControlRow>

                <DesignControlRow label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.qrBackground",
                  "QR background",
                )}>
                  <input
                    type="color"
                    value={normalizeHexColor(
                      selectedValue.background_color,
                      "#ffffff",
                    )}
                    onChange={(
                      event,
                    ) =>
                      updateSelected(
                        {
                          background_color:
                            event
                              .target
                              .value,
                        },
                      )
                    }
                  />
                </DesignControlRow>
              </div>
            </>
          ) : null}

          {selectedBlock.type ===
            "logo" ||
          selectedBlock.type ===
            "image" ? (
            <>
              <DesignNumberControl
                label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.imageWidth",
                  "Image width",
                )}
                value={
                  selectedValue.width ??
                  22
                }
                min={6}
                max={70}
                step={1}
                suffix="%"
                onChange={(
                  value,
                ) =>
                  updateSelected(
                    {
                      width:
                        value,
                    },
                  )
                }
              />

              <DesignNumberControl
                label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.opacity",
                  "Opacity",
                )}
                value={
                  selectedValue.opacity ??
                  1
                }
                min={0.1}
                max={1}
                step={0.05}
                onChange={(
                  value,
                ) =>
                  updateSelected(
                    {
                      opacity:
                        value,
                    },
                  )
                }
              />
            </>
          ) : null}

          {selectedBlock.type ===
          "nfc" ? (
            <>
              <DesignNumberControl
                label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.nfcSize",
                  "NFC size",
                )}
                value={
                  selectedValue.size ??
                  22
                }
                min={10}
                max={48}
                step={1}
                suffix="%"
                onChange={(
                  value,
                ) =>
                  updateSelected(
                    {
                      size:
                        value,
                    },
                  )
                }
              />

              <div className="qr-print-design-control-grid two-cols">
                <DesignControlRow label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.iconColor",
                  "Icon color",
                )}>
                  <input
                    type="color"
                    value={normalizeHexColor(
                      selectedValue.color,
                      PRIMARY_COLOR,
                    )}
                    onChange={(
                      event,
                    ) =>
                      updateSelected(
                        {
                          color:
                            event
                              .target
                              .value,
                        },
                      )
                    }
                  />
                </DesignControlRow>

                <DesignControlRow label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.background",
                  "Background",
                )}>
                  <input
                    type="color"
                    value={normalizeHexColor(
                      selectedValue.background_color,
                      PRIMARY_SOFT,
                    )}
                    onChange={(
                      event,
                    ) =>
                      updateSelected(
                        {
                          background_color:
                            event
                              .target
                              .value,
                        },
                      )
                    }
                  />
                </DesignControlRow>
              </div>
            </>
          ) : null}

          {selectedBlock.type ===
          "shape" ? (
            <>
              <DesignControlRow label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.shape",
                  "Shape",
                )}>
                <QrPrintFormSelect
                  value={
                    selectedValue.shape ||
                    "rectangle"
                  }
                  options={[
                    {
                      value:
                        "rectangle",
                      label:
                        translateFallback(
                          t,
                          "qrPrint.productForm.designStudio.rectangle",
                          "Rectangle",
                        ),
                    },
                    {
                      value:
                        "circle",
                      label:
                        translateFallback(
                          t,
                          "qrPrint.productForm.designStudio.circle",
                          "Circle",
                        ),
                    },
                    {
                      value:
                        "line",
                      label:
                        translateFallback(
                          t,
                          "qrPrint.productForm.designStudio.line",
                          "Line",
                        ),
                    },
                  ]}
                  onChange={(
                    value,
                  ) =>
                    updateSelected(
                      {
                        shape:
                          value,
                      },
                    )
                  }
                  label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.shape",
                  "Shape",
                )}
                />
              </DesignControlRow>

              <div className="qr-print-design-control-grid two-cols">
                <DesignNumberControl
                  label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.width",
                  "Width",
                )}
                  value={
                    selectedValue.width ??
                    28
                  }
                  min={2}
                  max={100}
                  step={1}
                  suffix="%"
                  onChange={(
                    value,
                  ) =>
                    updateSelected(
                      {
                        width:
                          value,
                      },
                    )
                  }
                />

                <DesignNumberControl
                  label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.height",
                  "Height",
                )}
                  value={
                    selectedValue.height ??
                    12
                  }
                  min={1}
                  max={100}
                  step={1}
                  suffix="%"
                  onChange={(
                    value,
                  ) =>
                    updateSelected(
                      {
                        height:
                          value,
                      },
                    )
                  }
                />
              </div>

              <div className="qr-print-design-control-grid two-cols">
                <DesignControlRow label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.fill",
                  "Fill",
                )}>
                  <input
                    type="color"
                    value={normalizeHexColor(
                      selectedValue.fill_color,
                      PRIMARY_SOFT,
                    )}
                    onChange={(
                      event,
                    ) =>
                      updateSelected(
                        {
                          fill_color:
                            event
                              .target
                              .value,
                        },
                      )
                    }
                  />
                </DesignControlRow>

                <DesignControlRow label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.border",
                  "Border",
                )}>
                  <input
                    type="color"
                    value={normalizeHexColor(
                      selectedValue.border_color,
                      PRIMARY_COLOR,
                    )}
                    onChange={(
                      event,
                    ) =>
                      updateSelected(
                        {
                          border_color:
                            event
                              .target
                              .value,
                        },
                      )
                    }
                  />
                </DesignControlRow>
              </div>

              <div className="qr-print-design-control-grid two-cols">
                <DesignNumberControl
                  label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.radius",
                  "Radius",
                )}
                  value={
                    selectedValue.radius ??
                    14
                  }
                  min={0}
                  max={50}
                  step={1}
                  onChange={(
                    value,
                  ) =>
                    updateSelected(
                      {
                        radius:
                          value,
                      },
                    )
                  }
                />

                <DesignNumberControl
                  label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.opacity",
                  "Opacity",
                )}
                  value={
                    selectedValue.opacity ??
                    1
                  }
                  min={0.1}
                  max={1}
                  step={0.05}
                  onChange={(
                    value,
                  ) =>
                    updateSelected(
                      {
                        opacity:
                          value,
                      },
                    )
                  }
                />
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}

function DesignControlRow({
  label,
  children,
}) {
  return (
    <label className="qr-print-design-control-row">
      <span>
        {label}
      </span>

      {children}
    </label>
  );
}

function DesignNumberControl({
  label,
  value,
  min,
  max,
  step,
  suffix = "",
  onChange,
}) {
  const safeValue =
    clampNumber(
      value,
      min,
      max,
      min,
    );

  return (
    <label className="qr-print-design-number-control">
      <span>
        {label}

        <small>
          {safeValue}
          {suffix}
        </small>
      </span>

      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={safeValue}
        onChange={(
          event,
        ) =>
          onChange(
            Number(
              event.target
                .value,
            ),
          )
        }
      />

      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={safeValue}
        dir="ltr"
        onChange={(
          event,
        ) =>
          onChange(
            clampNumber(
              event.target
                .value,
              min,
              max,
              safeValue,
            ),
          )
        }
      />
    </label>
  );
}

function QrPrint2DProductPreview({
  productName,
  productType,
  accessType,
  supportsBackDesign,
  editorSchema,
  selectedBlockId,
  onSelectBlock,
  onBlockChange,
  printSizeCm,
  previewZoom = 100,
}) {
  const { t } =
    useLanguage();

  const dragStateRef =
    useRef(null);

  const panStateRef =
    useRef(null);

  const normalizedSchema =
    normalizeQrEditorSchema(
      editorSchema,
    );

  const normalizedPrintSize =
    printSizeCm ||
    normalizePrintSizeCm(
      normalizedSchema,
      productType,
    );

  const safePreviewZoom =
    clampNumber(
      previewZoom,
      40,
      220,
      100,
    );

  const previewWidthPx =
    clampNumber(
      normalizedPrintSize.width *
        28,
      180,
      620,
      420,
    );

  const schemaBlocks =
    normalizedSchema.blocks ||
    [];

  const enabledBlocks =
    schemaBlocks.filter(
      (block) =>
        block &&
        block.enabled !== false,
    );

  const hasQrBlock =
    enabledBlocks.some(
      (block) =>
        block.type === "qr",
    );

  const hasNfcBlock =
    enabledBlocks.some(
      (block) =>
        block.type === "nfc",
    );

  const draggableBlocks =
    enabledBlocks.filter(
      (block) =>
        [
          "shape",
          "text",
          "logo",
          "image",
          "qr",
          "nfc",
        ].includes(
          block.type,
        ),
    );

  const backgroundBlock =
    enabledBlocks.find(
      (block) =>
        block.type ===
        "background_color",
    );

  const productColorBlock =
    enabledBlocks.find(
      (block) =>
        block.type ===
        "product_color",
    );

  const surfaceBackground =
    normalizeHexColor(
      backgroundBlock?.default,
      "#ffffff",
    );

  const productBorderColor =
    typeof productColorBlock?.default ===
      "string" &&
    productColorBlock.default
      ? productColorBlock.default
      : null;

  const sideDefinitions =
    supportsBackDesign
      ? [
          [
            "front",
            translateFallback(
              t,
              "qrPrint.productForm.designStudio.frontSide",
              "Front side",
            ),
          ],
          [
            "back",
            translateFallback(
              t,
              "qrPrint.productForm.designStudio.backSide",
              "Back side",
            ),
          ],
        ]
      : [
          [
            "front",
            translateFallback(
              t,
              "qrPrint.productForm.designStudio.frontSide",
              "Front side",
            ),
          ],
        ];

  const accessLabel =
    accessType === "qr"
      ? translateFallback(
          t,
          "qrPrint.catalog.technologyQr",
          "QR code",
        )
      : accessType === "nfc"
        ? translateFallback(
            t,
            "qrPrint.productForm.designStudio.nfcTap",
            "NFC tap",
          )
        : translateFallback(
            t,
            "qrPrint.catalog.technologyQrNfc",
            "QR + NFC",
          );

  const shouldShowNfcFallback =
    accessType !== "qr" &&
    !hasNfcBlock;

  const getPointerPosition = (
    event,
    surface,
    dragState,
  ) => {
    const rect =
      surface.getBoundingClientRect();

    const x =
      ((event.clientX -
        rect.left -
        dragState.offsetX) /
        rect.width) *
      100;

    const y =
      ((event.clientY -
        rect.top -
        dragState.offsetY) /
        rect.height) *
      100;

    return {
      x: roundSchemaNumber(
        clampNumber(
          x,
          dragState.paddingX,
          100 -
            dragState.paddingX,
          50,
        ),
      ),

      y: roundSchemaNumber(
        clampNumber(
          y,
          dragState.paddingY,
          100 -
            dragState.paddingY,
          50,
        ),
      ),
    };
  };

  const startDrag = (
    event,
    block,
  ) => {
    if (!onBlockChange) {
      return;
    }

    if (
      event.pointerType ===
        "mouse" &&
      event.button !== 0
    ) {
      return;
    }

    const surface =
      event.currentTarget.closest(
        '[data-qr-print-2d-surface="true"]',
      );

    if (!surface) {
      return;
    }

    onSelectBlock(
      block.id,
    );

    const rect =
      surface.getBoundingClientRect();

    const value =
      getSchemaElementValue(
        block,
      );

    const x =
      clampNumber(
        value.x,
        0,
        100,
        50,
      );

    const y =
      clampNumber(
        value.y,
        0,
        100,
        50,
      );

    const padding =
      getDragPadding(
        block,
        value,
      );

    const dragState = {
      blockId: block.id,
      surface,

      paddingX:
        padding.x,

      paddingY:
        padding.y,

      offsetX:
        event.clientX -
        (rect.left +
          (x / 100) *
            rect.width),

      offsetY:
        event.clientY -
        (rect.top +
          (y / 100) *
            rect.height),
    };

    const moveDrag = (
      moveEvent,
    ) => {
      if (
        !dragStateRef.current
      ) {
        return;
      }

      onBlockChange(
        dragState.blockId,

        getPointerPosition(
          moveEvent,
          dragState.surface,
          dragState,
        ),
      );
    };

    const stopDrag = () => {
      window.removeEventListener(
        "pointermove",
        moveDrag,
      );

      window.removeEventListener(
        "pointerup",
        stopDrag,
      );

      window.removeEventListener(
        "pointercancel",
        stopDrag,
      );

      dragStateRef.current =
        null;
    };

    dragState.cleanup =
      stopDrag;

    dragStateRef.current =
      dragState;

    window.addEventListener(
      "pointermove",
      moveDrag,
    );

    window.addEventListener(
      "pointerup",
      stopDrag,
    );

    window.addEventListener(
      "pointercancel",
      stopDrag,
    );

    try {
      event.currentTarget.setPointerCapture(
        event.pointerId,
      );
    } catch {
      // Window listeners keep dragging reliable.
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const startPreviewPan = (
    event,
  ) => {
    if (
      event.pointerType ===
        "mouse" &&
      event.button !== 0
    ) {
      return;
    }

    if (
      event.target?.closest(
        '[data-qr-print-draggable="true"]',
      )
    ) {
      return;
    }

    const stage =
      event.currentTarget;

    if (!stage) {
      return;
    }

    stage.focus({
      preventScroll: true,
    });

    const panState = {
      stage,

      startX:
        event.clientX,

      startY:
        event.clientY,

      scrollLeft:
        stage.scrollLeft,

      scrollTop:
        stage.scrollTop,
    };

    const movePan = (
      moveEvent,
    ) => {
      if (
        !panStateRef.current
      ) {
        return;
      }

      const deltaX =
        moveEvent.clientX -
        panState.startX;

      const deltaY =
        moveEvent.clientY -
        panState.startY;

      panState.stage.scrollLeft =
        panState.scrollLeft -
        deltaX;

      panState.stage.scrollTop =
        panState.scrollTop -
        deltaY;
    };

    const stopPan = () => {
      panState.stage.classList.remove(
        "is-panning",
      );

      window.removeEventListener(
        "pointermove",
        movePan,
      );

      window.removeEventListener(
        "pointerup",
        stopPan,
      );

      window.removeEventListener(
        "pointercancel",
        stopPan,
      );

      panStateRef.current =
        null;
    };

    panState.cleanup =
      stopPan;

    panStateRef.current =
      panState;

    stage.classList.add(
      "is-panning",
    );

    window.addEventListener(
      "pointermove",
      movePan,
    );

    window.addEventListener(
      "pointerup",
      stopPan,
    );

    window.addEventListener(
      "pointercancel",
      stopPan,
    );

    event.preventDefault();
  };

  const handlePreviewKeyDown = (
    event,
  ) => {
    const stage =
      event.currentTarget;

    const baseStep =
      event.shiftKey
        ? 120
        : 42;

    let handled = true;

    if (
      event.key ===
      "ArrowLeft"
    ) {
      stage.scrollLeft -=
        baseStep;
    } else if (
      event.key ===
      "ArrowRight"
    ) {
      stage.scrollLeft +=
        baseStep;
    } else if (
      event.key ===
      "ArrowUp"
    ) {
      stage.scrollTop -=
        baseStep;
    } else if (
      event.key ===
      "ArrowDown"
    ) {
      stage.scrollTop +=
        baseStep;
    } else {
      handled = false;
    }

    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  useEffect(
    () => () => {
      dragStateRef.current
        ?.cleanup?.();

      panStateRef.current
        ?.cleanup?.();
    },
    [],
  );

  const stopDrag = () => {
    dragStateRef.current
      ?.cleanup?.();
  };

  const renderPreviewBlock = (
    block,
    side,
  ) => (
    <QrPrint2DPreviewBlock
      key={`${side}-${block.id}`}
      block={block}
      productName={
        productName
      }
      selected={
        selectedBlockId ===
        block.id
      }
      onClick={() =>
        onSelectBlock(
          block.id,
        )
      }
      onPointerDown={(
        event,
      ) =>
        startDrag(
          event,
          block,
        )
      }
      onPointerUp={
        stopDrag
      }
      onPointerCancel={
        stopDrag
      }
    />
  );

  return (
    <div className="qr-print-design-preview-panel">
      <div className="qr-print-design-preview-panel__top">
        <div>
          <strong>
            {translateFallback(
              t,
              "qrPrint.productForm.designStudio.live2dPreview",
              "Live 2D preview",
            )}
          </strong>

          <span>
            {translateFallback(
              t,
              "qrPrint.productForm.designStudio.panHint",
              "Drag elements or grab the empty preview area to pan.",
            )}
          </span>
        </div>

        <em>
          {accessLabel} ·{" "}
          {Math.round(
            safePreviewZoom,
          )}
          %
        </em>
      </div>

      <div className="qr-print-2d-preview-grid">
        {sideDefinitions.map(
          ([
            side,
            sideLabel,
          ]) => (
            <article
              className="qr-print-2d-preview-side"
              key={side}
            >
              <div className="qr-print-2d-preview-side__header">
                <strong>
                  {sideLabel}
                </strong>

                <span>
                  {formatProductTypeLabel(
                    t,
                    productType,
                  )}
                </span>
              </div>

              <div
                className="qr-print-2d-preview-stage"
                tabIndex={0}
                role="region"
                aria-label={translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.previewPanArea",
                  "{{side}} zoomed preview pan area",
                  { side: sideLabel },
                )}
                onPointerDown={
                  startPreviewPan
                }
                onKeyDown={
                  handlePreviewKeyDown
                }
              >
                <div
                  className="qr-print-2d-print-size-frame"
                  style={{
                    "--qr-print-preview-zoom":
                      safePreviewZoom /
                      100,

                    "--qr-print-preview-width":
                      `${previewWidthPx}px`,

                    "--qr-print-preview-aspect":
                      `${normalizedPrintSize.width} / ${normalizedPrintSize.height}`,
                  }}
                >
                  <div className="qr-print-2d-print-size-top-row">
                    <div
                      className="qr-print-2d-size-ruler-spacer"
                      aria-hidden="true"
                    />

                    <div className="qr-print-2d-size-ruler qr-print-2d-size-ruler-width">
                      <span>
                        &lt;---{" "}
                        {formatCm(
                          normalizedPrintSize.width,
                        )}{" "}
                        ---&gt;
                      </span>
                    </div>
                  </div>

                  <div className="qr-print-2d-print-size-row">
                    <div className="qr-print-2d-size-ruler qr-print-2d-size-ruler-height">
                      <span>
                        &lt;---{" "}
                        {formatCm(
                          normalizedPrintSize.height,
                        )}{" "}
                        ---&gt;
                      </span>
                    </div>

                    <div
                      className={`qr-print-2d-preview-surface is-${
                        productType ||
                        "table_stand"
                      }`}
                      data-qr-print-2d-surface="true"
                      style={{
                        "--qr-print-preview-width":
                          `${previewWidthPx}px`,

                        "--qr-print-preview-aspect":
                          `${normalizedPrintSize.width} / ${normalizedPrintSize.height}`,

                        aspectRatio:
                          `${normalizedPrintSize.width} / ${normalizedPrintSize.height}`,

                        backgroundColor:
                          surfaceBackground,

                        borderColor:
                          productBorderColor ||
                          undefined,
                      }}
                    >
                      {draggableBlocks.map(
                        (block) =>
                          renderPreviewBlock(
                            block,
                            side,
                          ),
                      )}

                      {shouldShowNfcFallback ? (
                        <QrPrint2DPreviewBlock
                          block={{
                            id: "fallback-nfc",
                            type: "nfc",
                            label:
                              translateFallback(
                                t,
                                "qrPrint.productForm.designStudio.nfcTapZone",
                                "NFC tap zone",
                              ),

                            default: {
                              x: hasQrBlock
                                ? 72
                                : 50,

                              y: hasQrBlock
                                ? 55
                                : 50,

                              size:
                                hasQrBlock
                                  ? 18
                                  : 24,

                              color:
                                PRIMARY_COLOR,

                              background_color:
                                PRIMARY_SOFT,
                            },
                          }}
                          productName={
                            productName
                          }
                          selected={
                            false
                          }
                          onClick={() => {}}
                          onPointerDown={() => {}}
                          onPointerUp={() => {}}
                          onPointerCancel={() => {}}
                        />
                      ) : null}

                      {!hasQrBlock &&
                      !hasNfcBlock &&
                      accessType ===
                        "qr" ? (
                        <div className="qr-print-2d-preview-empty">
                          {translateFallback(
                            t,
                            "qrPrint.productForm.designStudio.noQrBlock",
                            "No QR block enabled",
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ),
        )}
      </div>
    </div>
  );
}

function QrPrint2DPreviewBlock({
  block,
  productName,
  selected,
  onClick,
  onPointerDown,
  onPointerUp,
  onPointerCancel,
}) {
  const { t } =
    useLanguage();

  const value =
    getSchemaElementValue(
      block,
    );

  const className =
    `qr-print-2d-preview-element is-draggable ${
      selected
        ? "is-selected"
        : ""
    }`;

  if (
    block.type === "shape"
  ) {
    const width =
      clampNumber(
        value.width,
        2,
        100,
        28,
      );

    const height =
      clampNumber(
        value.height,
        1,
        100,
        12,
      );

    const shape =
      value.shape ||
      "rectangle";

    return (
      <button
        type="button"
        className={`${className} qr-print-2d-preview-shape is-${shape}`}
        data-qr-print-draggable="true"
        style={{
          left: `${clampNumber(
            value.x,
            0,
            100,
            50,
          )}%`,

          top: `${clampNumber(
            value.y,
            0,
            100,
            50,
          )}%`,

          width:
            `${width}%`,

          height:
            `${height}%`,

          borderRadius:
            shape ===
            "circle"
              ? "999px"
              : shape ===
                  "line"
                ? "999px"
                : `${clampNumber(
                    value.radius,
                    0,
                    50,
                    14,
                  )}px`,

          backgroundColor:
            value.fill_color ||
            PRIMARY_SOFT,

          borderColor:
            value.border_color ||
            PRIMARY_COLOR,

          opacity:
            value.opacity ?? 1,
        }}
        onClick={onClick}
        onPointerDown={
          onPointerDown
        }
        onPointerUp={
          onPointerUp
        }
        onPointerCancel={
          onPointerCancel
        }
      />
    );
  }

  if (
    block.type === "text"
  ) {
    const text =
      value.text ||
      (block.id ===
      "restaurant-name"
        ? productName ||
          translateFallback(
            t,
            "qrPrint.productForm.designStudio.restaurantName",
            "Restaurant name",
          )
        : getEditorBlockDisplayLabel(
            t,
            block,
          ) ||
          translateFallback(
            t,
            "qrPrint.productForm.designStudio.text",
            "Text",
          ));

    const align =
      value.align ||
      "center";

    const fontSize =
      clampNumber(
        value.font_size,
        1.4,
        12,
        4,
      );

    return (
      <button
        type="button"
        className={`${className} qr-print-2d-preview-text`}
        data-qr-print-draggable="true"
        style={{
          left: `${clampNumber(
            value.x,
            0,
            100,
            50,
          )}%`,

          top: `${clampNumber(
            value.y,
            0,
            100,
            50,
          )}%`,

          transform:
            getTextTransform(
              align,
            ),

          color:
            value.color ||
            "#1f2937",

          fontSize:
            `${fontSize * 3.4}px`,

          fontWeight:
            Number(
              value.weight ||
              700,
            ),

          textAlign:
            align,
        }}
        onClick={onClick}
        onPointerDown={
          onPointerDown
        }
        onPointerUp={
          onPointerUp
        }
        onPointerCancel={
          onPointerCancel
        }
      >
        {text}
      </button>
    );
  }

  if (
    block.type === "qr"
  ) {
    const size =
      clampNumber(
        value.size,
        12,
        72,
        34,
      );

    return (
      <button
        type="button"
        className={`${className} qr-print-2d-preview-qr-real`}
        data-qr-print-draggable="true"
        style={{
          left: `${clampNumber(
            value.x,
            0,
            100,
            50,
          )}%`,

          top: `${clampNumber(
            value.y,
            0,
            100,
            50,
          )}%`,

          width:
            `${size}%`,

          backgroundColor:
            value.background_color ||
            "#ffffff",
        }}
        onClick={onClick}
        onPointerDown={
          onPointerDown
        }
        onPointerUp={
          onPointerUp
        }
        onPointerCancel={
          onPointerCancel
        }
      >
        <QRCodeSVG
          value="https://menu.example/restaurant"
          size={512}
          level="H"
          marginSize={0}
          bgColor={
            value.background_color ||
            "#ffffff"
          }
          fgColor={
            value.foreground_color ||
            "#111111"
          }
        />
      </button>
    );
  }

  if (
    block.type === "nfc"
  ) {
    const size =
      clampNumber(
        value.size,
        10,
        48,
        22,
      );

    return (
      <button
        type="button"
        className={`${className} qr-print-2d-preview-nfc-real`}
        data-qr-print-draggable="true"
        style={{
          left: `${clampNumber(
            value.x,
            0,
            100,
            50,
          )}%`,

          top: `${clampNumber(
            value.y,
            0,
            100,
            50,
          )}%`,

          width:
            `${size}%`,

          color:
            value.color ||
            PRIMARY_COLOR,

          backgroundColor:
            value.background_color ||
            PRIMARY_SOFT,
        }}
        onClick={onClick}
        onPointerDown={
          onPointerDown
        }
        onPointerUp={
          onPointerUp
        }
        onPointerCancel={
          onPointerCancel
        }
      >
        <Nfc size={28} />

        <span>
          {translateFallback(
            t,
            "qrPrint.productForm.designStudio.nfc",
            "NFC",
          )}
        </span>
      </button>
    );
  }

  if (
    block.type === "logo" ||
    block.type === "image"
  ) {
    const width =
      clampNumber(
        value.width,
        6,
        70,
        24,
      );

    return (
      <button
        type="button"
        className={`${className} qr-print-2d-preview-image`}
        data-qr-print-draggable="true"
        style={{
          left: `${clampNumber(
            value.x,
            0,
            100,
            50,
          )}%`,

          top: `${clampNumber(
            value.y,
            0,
            100,
            50,
          )}%`,

          width:
            `${width}%`,

          opacity:
            value.opacity ?? 1,
        }}
        onClick={onClick}
        onPointerDown={
          onPointerDown
        }
        onPointerUp={
          onPointerUp
        }
        onPointerCancel={
          onPointerCancel
        }
      >
        {value.src ? (
          <img
            src={value.src}
            alt={getEditorBlockDisplayLabel(
              t,
              block,
            )}
          />
        ) : (
          <span>
            {block.type ===
            "logo"
              ? translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.logo",
                  "LOGO",
                )
              : translateFallback(
                  t,
                  "qrPrint.productForm.designStudio.image",
                  "IMAGE",
                )}
          </span>
        )}
      </button>
    );
  }

  return null;
}

function OptionSection({
  icon,
  title,
  description,
  items,
  onAdd,
  onChange,
  onRemove,
}) {
  const { t } =
    useLanguage();

  return (
    <section className="qr-print-form-section qr-print-form-section--options">
      <SectionTitle
        icon={icon}
        title={title}
        description={
          description
        }
        action={
          <button
            type="button"
            className="qr-print-icon-button"
            onClick={onAdd}
          >
            <Plus
              size={18}
            />
          </button>
        }
      />

      <div className="qr-print-option-list">
        {items.map(
          (
            option,
            index,
          ) => (
            <div
              className="qr-print-option-row"
              key={`${title}-${index}`}
            >
              <input
                dir="ltr"
                value={
                  option.value ||
                  ""
                }
                placeholder={translateFallback(
                  t,
                  "qrPrint.productForm.optionValue",
                  "Value",
                )}
                onChange={(
                  event,
                ) =>
                  onChange(
                    index,
                    "value",
                    event.target
                      .value,
                  )
                }
              />

              <input
                value={
                  option.label ||
                  ""
                }
                placeholder={translateFallback(
                  t,
                  "qrPrint.productForm.optionLabel",
                  "Label",
                )}
                onChange={(
                  event,
                ) =>
                  onChange(
                    index,
                    "label",
                    event.target
                      .value,
                  )
                }
              />

              <button
                type="button"
                className="qr-print-icon-button is-danger"
                aria-label="Remove option"
                title="Remove option"
                onClick={() =>
                  onRemove(
                    index,
                  )
                }
              >
                <Trash2
                  size={17}
                />
              </button>
            </div>
          ),
        )}
      </div>
    </section>
  );
}